// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Permission keys for RBAC enforcement.
 * See: docs/security/permission-matrix.md
 *
 * Sprint 1.0.0 + 1.1.0 + 1.2.0 permissions.
 */
export const Permission = {
    // Case Operations (Group B)
    CASE_CREATE: 'case:create',
    CASE_READ: 'case:read',
    CASE_UPDATE: 'case:update',
    CASE_DELETE: 'case:delete',
    CASE_ASSIGN: 'case:assign',
    CASE_REASSIGN: 'case:reassign',
    CASE_COMMENT: 'case:comment',
    CASE_ATTACH: 'case:attach',
    CASE_ATTACH_DOWNLOAD: 'case:attach:download',
    // Task Operations
    TASK_CREATE: 'task:create',
    TASK_READ: 'task:read',
    TASK_UPDATE: 'task:update',
    TASK_COMPLETE: 'task:complete',
    // Template Operations
    TEMPLATE_READ: 'template:read',
    TEMPLATE_WRITE: 'template:write',
    // Rights Requests Specific (Group C)
    DSR_INTAKE_CONFIGURE: 'dsr:intake:configure',
    DSR_IDENTITY_VERIFY: 'dsr:identity:verify',
    DSR_RESPONSE_DRAFT: 'dsr:response:draft',
    DSR_RESPONSE_SEND: 'dsr:response:send',
    DSR_SLA_CONFIGURE: 'dsr:sla:configure',
    DSR_SLA_OVERRIDE: 'dsr:sla:override',
    // SLA Config (Sprint 1.1.0 - Epic A)
    SLA_CONFIG_READ: 'sla:config:read',
    SLA_CONFIG_WRITE: 'sla:config:write',
    DSR_DATA_COLLECTION_REQUEST: 'dsr:data_collection:request',
    DSR_DELETION_REQUEST: 'dsr:deletion:request',
    DSR_DELETION_CONFIRM: 'dsr:deletion:confirm',
    // Sprint 1.1.0 additions
    CASE_ATTACH_DELETE: 'case:attach:delete',
    CASE_CHECKLIST_UPDATE: 'case:checklist:update',
    CASE_REFERENCE_ADD: 'case:reference:add',
    CASE_CLOSURE_SUBMIT: 'case:closure:submit',
    CASE_CLOSURE_APPROVE: 'case:closure:approve',
    CASE_EXPORT_PDF: 'case:export:pdf',
    CASE_EXPORT_ZIP: 'case:export:zip',
    // Audit (Group I)
    AUDIT_EVENTS_READ: 'audit:events:read',
    AUDIT_EVENTS_SEARCH: 'audit:events:search',
    AUDIT_EVENTS_EXPORT: 'audit:events:export',
    AUDIT_DASHBOARD_READ: 'audit:dashboard:read',
    // Platform (Group A)
    PLATFORM_SETTINGS_READ: 'platform:settings:read',
    PLATFORM_SETTINGS_WRITE: 'platform:settings:write',
    PLATFORM_SECURITY_READ: 'platform:security:read',
    PLATFORM_SECURITY_WRITE: 'platform:security:write',
    PLATFORM_USERS_READ: 'platform:users:read',
    PLATFORM_USERS_WRITE: 'platform:users:write',
    PLATFORM_USERS_ROLES_ASSIGN: 'platform:users:roles:assign',
    PLATFORM_SERVICE_ACCOUNTS_READ: 'platform:service_accounts:read',
    PLATFORM_SERVICE_ACCOUNTS_WRITE: 'platform:service_accounts:write',
    PLATFORM_INTEGRATIONS_READ: 'platform:integrations:read',
    PLATFORM_INTEGRATIONS_WRITE: 'platform:integrations:write',
    PLATFORM_JOBS_READ: 'platform:jobs:read',
    PLATFORM_JOBS_MANAGE: 'platform:jobs:manage',
    // Sprint 3.1.0: MFA & SSO
    PLATFORM_MFA_READ: 'platform:mfa:read',
    PLATFORM_MFA_WRITE: 'platform:mfa:write',
    PLATFORM_SSO_READ: 'platform:sso:read',
    PLATFORM_SSO_WRITE: 'platform:sso:write',
    // Sprint 3.2.0: API Keys, Delegations, Break-Glass
    PLATFORM_API_KEYS_READ: 'platform:api-keys:read',
    PLATFORM_API_KEYS_WRITE: 'platform:api-keys:write',
    PLATFORM_DELEGATIONS_READ: 'platform:delegations:read',
    PLATFORM_DELEGATIONS_WRITE: 'platform:delegations:write',
    PLATFORM_BREAKGLASS_REQUEST: 'platform:breakglass:request',
    PLATFORM_BREAKGLASS_APPROVE: 'platform:breakglass:approve',
    // Sprint 3.3.0: Access Review, Attestation, SoD
    PLATFORM_ACCESS_REVIEW_READ: 'platform:access-review:read',
    PLATFORM_ACCESS_REVIEW_WRITE: 'platform:access-review:write',
    PLATFORM_ACCESS_REVIEW_DECIDE: 'platform:access-review:decide',
    PLATFORM_ATTESTATION_READ: 'platform:attestation:read',
    PLATFORM_ATTESTATION_WRITE: 'platform:attestation:write',
    PLATFORM_SOD_READ: 'platform:sod:read',
    PLATFORM_SOD_WRITE: 'platform:sod:write',
    // Sprint 41.4.0 F1 — Platform → Files Configuration & Browser
    // 10 new permissions backing the FilesConfigPage, FilesBrowserPage,
    // PlatformFileDetailModal, FilesDashboardPage. See sprint-41.4.0.md
    // §"Epic F1 — Permissions" for the role-tier mapping rationale.
    PLATFORM_FILES_CONFIG_READ: 'platform:files:config:read',
    PLATFORM_FILES_CONFIG_WRITE: 'platform:files:config:write',
    PLATFORM_FILES_BROWSE: 'platform:files:browse',
    PLATFORM_FILES_DETAIL_READ: 'platform:files:detail:read',
    PLATFORM_FILES_SOFT_DELETE: 'platform:files:soft_delete',
    PLATFORM_FILES_RESTORE: 'platform:files:restore',
    PLATFORM_FILES_HARD_DELETE: 'platform:files:hard_delete',
    PLATFORM_FILES_AUDIT_READ: 'platform:files:audit:read',
    PLATFORM_FILES_METRICS_READ: 'platform:files:metrics:read',
    PLATFORM_FILES_LEGAL_HOLD_OVERRIDE: 'platform:files:legal_hold:override',
    // Data Mapping (Sprint 1.2.0)
    DM_SYSTEM_READ: 'dm:system:read',
    DM_SYSTEM_WRITE: 'dm:system:write',
    DM_SYSTEM_ARCHIVE: 'dm:system:archive',
    DM_CATEGORY_READ: 'dm:category:read',
    DM_CATEGORY_WRITE: 'dm:category:write',
    DM_PURPOSE_READ: 'dm:purpose:read',
    DM_PURPOSE_WRITE: 'dm:purpose:write',
    DM_RETENTION_READ: 'dm:retention:read',
    DM_RETENTION_WRITE: 'dm:retention:write',
    DM_FLOW_READ: 'dm:flow:read',
    DM_FLOW_WRITE: 'dm:flow:write',
    DM_FLOW_ARCHIVE: 'dm:flow:archive',
    DM_ACTIVITY_READ: 'dm:activity:read',
    DM_ACTIVITY_WRITE: 'dm:activity:write',
    DM_ACTIVITY_ARCHIVE: 'dm:activity:archive',
    DM_ROPA_READ: 'dm:ropa:read',
    DM_ROPA_GENERATE: 'dm:ropa:generate',
    DM_EVIDENCE_READ: 'dm:evidence:read',
    DM_EVIDENCE_WRITE: 'dm:evidence:write',
    DM_EVIDENCE_DELETE: 'dm:evidence:delete',
    DM_EVIDENCE_LINK: 'dm:evidence:link',
    DM_EVIDENCE_UNLINK: 'dm:evidence:unlink',
    DM_EXPORT_CSV: 'dm:export:csv',
    DM_EXPORT_PDF: 'dm:export:pdf',
    DM_AUDIT_READ: 'dm:audit:read',
    // Sprint 45.4.0 — Data Registry (Light-tier sub-module of Data Mapping)
    DM_REGISTRY_READ: 'dm:registry:read',
    DM_REGISTRY_WRITE: 'dm:registry:write',
    DM_REGISTRY_ARCHIVE: 'dm:registry:archive',
    DM_REGISTRY_EXPORT: 'dm:registry:export',
    DM_REGISTRY_IMPORT_FORM: 'dm:registry:import_form',
    // Sprint 44.3.1 Epic C — DM_DEFAULTS_MANAGE deleted (dead perm; no
    // consumers in code). Migration script at 44.3.1-perm-cleanup removes
    // 'dm:defaults:manage' from any persisted custom-role permissions[].
    // Data Mapping Attestation (Sprint 20.5.0)
    DM_ATTESTATION_READ: 'dm:attestation:read',
    DM_ATTESTATION_WRITE: 'dm:attestation:write',
    DM_OWNERSHIP_READ: 'dm:ownership:read',
    DM_OWNERSHIP_WRITE: 'dm:ownership:write',
    DM_RISK_ASSESSMENT_READ: 'dm:risk-assessment:read',
    DM_RISK_ASSESSMENT_WRITE: 'dm:risk-assessment:write',
    DM_RISK_ASSESSMENT_APPROVE: 'dm:risk-assessment:approve',
    DM_ATTESTATION_EXPORT: 'dm:attestation:export',
    // Data Mapping Discovery Workflow (Sprint 27.8.1)
    DM_CATEGORY_APPROVE: 'dm:category:approve',
    DM_REVIEW_READ: 'dm:review:read',
    DM_REVIEW_WRITE: 'dm:review:write',
    DM_INVENTORY_READ: 'dm:inventory:read',
    DM_EXCEPTION_READ: 'dm:exception:read',
    DM_EXCEPTION_WRITE: 'dm:exception:write',
    DM_HISTORY_READ: 'dm:history:read',
    // Governance (Sprint 1.3.0)
    GOV_VENDOR_READ: 'gov:vendor:read',
    GOV_VENDOR_WRITE: 'gov:vendor:write',
    GOV_VENDOR_APPROVE: 'gov:vendor:approve',
    GOV_VENDOR_OFFBOARD: 'gov:vendor:offboard',
    GOV_SUBPROCESSOR_READ: 'gov:subprocessor:read',
    GOV_SUBPROCESSOR_WRITE: 'gov:subprocessor:write',
    GOV_TRANSFER_READ: 'gov:transfer:read',
    GOV_TRANSFER_WRITE: 'gov:transfer:write',
    GOV_CROSSBORDER_READ: 'gov:crossborder:read',
    GOV_CROSSBORDER_EXPORT: 'gov:crossborder:export',
    // Sprint 41.5.0 G1 — Cross-Border Filing Lifecycle. Each permission
    // gates a distinct verb on grc_cross_border_filings so the DPO,
    // Cross-Border Lead, Internal Auditor, and CEO personas all map to
    // distinct ACL slices per UC-VN-07.
    GOV_CROSS_BORDER_FILING_READ: 'gov:crossborder:filing:read',
    GOV_CROSS_BORDER_FILING_WRITE: 'gov:crossborder:filing:write',
    GOV_CROSS_BORDER_FILING_SUBMIT: 'gov:crossborder:filing:submit',
    GOV_CROSS_BORDER_FILING_ARCHIVE: 'gov:crossborder:filing:archive',
    GOV_DRIFT_READ: 'gov:drift:read',
    GOV_DRIFT_MANAGE: 'gov:drift:manage',
    GOV_DRIFT_ACCEPT_RISK: 'gov:drift:accept_risk',
    GOV_SCORE_READ: 'gov:score:read',
    GOV_SCORE_WRITE: 'gov:score:write',
    GOV_PLAYBOOK_READ: 'gov:playbook:read',
    GOV_PLAYBOOK_WRITE: 'gov:playbook:write',
    GOV_PLAYBOOK_DELETE: 'gov:playbook:delete',
    GOV_EXECUTION_READ: 'gov:execution:read',
    GOV_EXECUTION_INITIATE: 'gov:execution:initiate',
    GOV_EXECUTION_STEP: 'gov:execution:step',
    GOV_APPROVAL_READ: 'gov:approval:read',
    GOV_APPROVAL_DECIDE: 'gov:approval:decide',
    GOV_EVIDENCE_READ: 'gov:evidence:read',
    GOV_EVIDENCE_WRITE: 'gov:evidence:write',
    GOV_EVIDENCE_DELETE: 'gov:evidence:delete',
    GOV_EVIDENCE_LINK: 'gov:evidence:link',
    GOV_AUDIT_READ: 'gov:audit:read',
    GOV_EXPORT_CSV: 'gov:export:csv',
    GOV_EXPORT_PDF: 'gov:export:pdf',
    GOV_EXPORT_EXECUTE: 'gov:export:execute',
    // Vendor Governance Maturity (Sprint 20.9.0)
    GOV_ONBOARDING_READ: 'gov:onboarding:read',
    GOV_ONBOARDING_WRITE: 'gov:onboarding:write',
    GOV_DPA_REVIEW_READ: 'gov:dpa_review:read',
    GOV_DPA_REVIEW_WRITE: 'gov:dpa_review:write',
    GOV_PERIODIC_REVIEW_READ: 'gov:periodic_review:read',
    GOV_PERIODIC_REVIEW_WRITE: 'gov:periodic_review:write',
    GOV_OFFBOARDING_READ: 'gov:offboarding:read',
    GOV_OFFBOARDING_WRITE: 'gov:offboarding:write',
    // Consent Governance (Sprint 1.4.0)
    CONSENT_POLICY_READ: 'consent_policy:read',
    CONSENT_POLICY_WRITE: 'consent_policy:write',
    CONSENT_POLICY_APPROVE: 'consent_policy:approve',
    CONSENT_POLICY_DELETE: 'consent_policy:delete',
    CONSENT_WORKFLOW_READ: 'consent_workflow:read',
    CONSENT_WORKFLOW_WRITE: 'consent_workflow:write',
    CONSENT_CMP_SNAPSHOT_READ: 'consent_cmp_snapshot:read',
    CONSENT_CMP_SNAPSHOT_WRITE: 'consent_cmp_snapshot:write',
    CONSENT_EXPORT_EXECUTE: 'consent_export:execute',
    // Consent Governance Maturity (Sprint 20.6.0)
    CONSENT_PROPAGATION_READ: 'consent_propagation:read',
    CONSENT_PROPAGATION_WRITE: 'consent_propagation:write',
    CONSENT_RECONCILIATION_READ: 'consent_reconciliation:read',
    CONSENT_RECONCILIATION_WRITE: 'consent_reconciliation:write',
    CONSENT_SYNC_READ: 'consent_sync:read',
    CONSENT_SYNC_WRITE: 'consent_sync:write',
    // Assessments (Sprint 1.5.0)
    DPIA_READ: 'dpia:read',
    DPIA_WRITE: 'dpia:write',
    DPIA_APPROVE: 'dpia:approve',
    DPIA_DELETE: 'dpia:delete',
    /**
     * PDPL evidence-pack authority submit (fix-sprint-20260619-0053-followup-2 #10).
     * Gates the irreversible /send + authority-response + hard-copy-followup routes
     * that transmit the assembled 8-part ZIP to MoPS A05. Split out from DPIA_WRITE
     * so that drafting, editing, and sent-proof upload (still gated by DPIA_WRITE)
     * can be delegated broadly while the actual transmission remains restricted
     * to org_admin / DPO by default.
     */
    PDPL_EVIDENCE_PACK_SEND: 'pdpl_evidence_pack:send',
    TRANSFER_ASSESSMENT_READ: 'transfer_assessment:read',
    TRANSFER_ASSESSMENT_WRITE: 'transfer_assessment:write',
    TRANSFER_ASSESSMENT_APPROVE: 'transfer_assessment:approve',
    TRANSFER_ASSESSMENT_DELETE: 'transfer_assessment:delete',
    // Sprint 44.3.1 Epic C — ASSESSMENT_EXPORT_EXECUTE deleted (dead perm;
    // no consumers). Migration removes 'assessment_export:execute' from
    // custom roles.
    // Sprint 40.1.0: Assessment Trigger Configuration
    ASSESSMENT_TRIGGER_CONFIGURE: 'assessment:trigger:configure',
    // Classification & Lifecycle (Sprint 1.6.0)
    TAXONOMY_READ: 'taxonomy:read',
    TAXONOMY_WRITE: 'taxonomy:write',
    TAXONOMY_DELETE: 'taxonomy:delete',
    CLASSIFICATION_READ: 'classification:read',
    CLASSIFICATION_WRITE: 'classification:write',
    CLASSIFICATION_REVIEW: 'classification:review',
    LIFECYCLE_READ: 'lifecycle:read',
    LIFECYCLE_WRITE: 'lifecycle:write',
    LIFECYCLE_HOLD: 'lifecycle:hold',
    GAP_REPORT_READ: 'gap_report:read',
    GAP_REPORT_MANAGE: 'gap_report:manage',
    CLASSIFICATION_EXPORT_EXECUTE: 'classification_export:execute',
    // Monitoring & Evidence Program (Sprint 1.7.0)
    PROGRAM_READ: 'program:read',
    PROGRAM_WRITE: 'program:write',
    PROGRAM_MANAGE: 'program:manage',
    REVIEW_TASK_READ: 'review_task:read',
    REVIEW_TASK_WRITE: 'review_task:write',
    REVIEW_TASK_ASSIGN: 'review_task:assign',
    TASK_TEMPLATE_READ: 'task_template:read',
    TASK_TEMPLATE_WRITE: 'task_template:write',
    KPI_READ: 'kpi:read',
    KPI_WRITE: 'kpi:write',
    RIC_READ: 'ric:read',
    RIC_WRITE: 'ric:write',
    // Sprint 44.3.1 Epic C — PROGRAM_EXPORT_EXECUTE deleted (dead perm;
    // superseded by monitoring_export:execute). Migration removes
    // 'program_export:execute' from custom roles.
    // Monitoring Programs Maturity (Sprint 21.3.0)
    MONITORING_FRAMEWORK_LINK_READ: 'monitoring_framework_link:read',
    MONITORING_FRAMEWORK_LINK_WRITE: 'monitoring_framework_link:write',
    MONITORING_AUTHORITY_READINESS_READ: 'monitoring_authority_readiness:read',
    MONITORING_AUTHORITY_READINESS_WRITE: 'monitoring_authority_readiness:write',
    MONITORING_CHANGE_REVIEW_READ: 'monitoring_change_review:read',
    MONITORING_CHANGE_REVIEW_WRITE: 'monitoring_change_review:write',
    // Monitoring Operational Completeness (Sprint 27.7.1)
    MONITORING_EXPORT: 'monitoring_export:execute',
    MONITORING_RACI_READ: 'monitoring_raci:read',
    MONITORING_RACI_WRITE: 'monitoring_raci:write',
    // Incident & Breach Response (Sprint 1.8.0)
    INCIDENT_READ: 'incident:read',
    INCIDENT_WRITE: 'incident:write',
    INCIDENT_TRIAGE: 'incident:triage',
    INCIDENT_ASSESS: 'incident:assess',
    INCIDENT_CLOSE: 'incident:close',
    INCIDENT_TASK_READ: 'incident_task:read',
    INCIDENT_TASK_WRITE: 'incident_task:write',
    INCIDENT_TASK_ASSIGN: 'incident_task:assign',
    INCIDENT_DECISION_WRITE: 'incident_decision:write',
    INCIDENT_NOTIFICATION_MANAGE: 'incident_notification:manage',
    INCIDENT_EXPORT_EXECUTE: 'incident_export:execute',
    // Onboarding & Enablement (Sprint 1.9.0)
    ONBOARDING_WIZARD_READ: 'onboarding_wizard:read',
    ONBOARDING_WIZARD_WRITE: 'onboarding_wizard:write',
    ONBOARDING_TOUR_READ: 'onboarding_tour:read',
    ONBOARDING_TOUR_WRITE: 'onboarding_tour:write',
    SAMPLE_DATA_READ: 'sample_data:read',
    SAMPLE_DATA_LOAD: 'sample_data:load',
    SAMPLE_DATA_CLEAR: 'sample_data:clear',
    HELP_ARTICLE_READ: 'help_article:read',
    HELP_ARTICLE_WRITE: 'help_article:write',
    HELP_ARTICLE_DELETE: 'help_article:delete',
    // 44.1.0 — FAQ Q&A editing on Help Center articles. Full admin surface
    // lands in a later phase; permission + stub routes scaffolded here.
    HELP_FAQ_EDIT: 'help_faq:edit',
    // Connectors Foundation (Sprint 2.0.0)
    CONNECTOR_READ: 'connector:read',
    CONNECTOR_WRITE: 'connector:write',
    CONNECTOR_MANAGE: 'connector:manage',
    CONNECTOR_TEST: 'connector:test',
    CREDENTIAL_READ: 'credential:read',
    CREDENTIAL_WRITE: 'credential:write',
    EVENT_BUS_READ: 'event_bus:read',
    EVENT_BUS_MANAGE: 'event_bus:manage',
    IDENTITY_SSO_CONFIGURE: 'identity_sso:configure',
    STORAGE_CONNECTOR_CONFIGURE: 'storage_connector:configure',
    // Vendor Risk Scoring & Procurement Governance (Sprint 2.1.0)
    VENDOR_RISK_CONFIG_READ: 'vendor_risk_config:read',
    VENDOR_RISK_CONFIG_WRITE: 'vendor_risk_config:write',
    VENDOR_RISK_SCORE_READ: 'vendor_risk_score:read',
    VENDOR_RISK_SCORE_WRITE: 'vendor_risk_score:write',
    VENDOR_QUESTIONNAIRE_TEMPLATE_READ: 'vendor_questionnaire_template:read',
    VENDOR_QUESTIONNAIRE_TEMPLATE_WRITE: 'vendor_questionnaire_template:write',
    VENDOR_QUESTIONNAIRE_READ: 'vendor_questionnaire:read',
    VENDOR_QUESTIONNAIRE_WRITE: 'vendor_questionnaire:write',
    VENDOR_QUESTIONNAIRE_REVIEW: 'vendor_questionnaire:review',
    VENDOR_CONTRACT_READ: 'vendor_contract:read',
    VENDOR_CONTRACT_WRITE: 'vendor_contract:write',
    // Sprint 44.3.1 Epic D (P1-04) — added so auditor (and other read-scope
    // roles) can view gate evaluations without carrying the write EVALUATE
    // perm. Previously GateEvaluationsPage gated view on EVALUATE which
    // blocked auditors by design.
    VENDOR_GATE_READ: 'vendor_gate:read',
    VENDOR_GATE_EVALUATE: 'vendor_gate:evaluate',
    VENDOR_GATE_OVERRIDE: 'vendor_gate:override',
    VENDOR_REVIEW_READ: 'vendor_review:read',
    VENDOR_REVIEW_WRITE: 'vendor_review:write',
    VENDOR_RISK_EXPORT_EXECUTE: 'vendor_risk_export:execute',
    // Vendor Risk Maturity (Sprint 21.0.0)
    VENDOR_RISK_SIGNAL_READ: 'vendor_risk_signal:read',
    VENDOR_RISK_SIGNAL_WRITE: 'vendor_risk_signal:write',
    VENDOR_RISK_REASSESSMENT_READ: 'vendor_risk_reassessment:read',
    VENDOR_RISK_REASSESSMENT_WRITE: 'vendor_risk_reassessment:write',
    VENDOR_RISK_ACCEPTANCE_READ: 'vendor_risk_acceptance:read',
    VENDOR_RISK_ACCEPTANCE_WRITE: 'vendor_risk_acceptance:write',
    VENDOR_RISK_PORTFOLIO_READ: 'vendor_risk_portfolio:read',
    VENDOR_RISK_REVIEW_ROUTING_READ: 'vendor_risk_review_routing:read',
    VENDOR_RISK_REVIEW_ROUTING_WRITE: 'vendor_risk_review_routing:write',
    // Vendor Onboarding Lifecycle (Sprint 28.3.1)
    VENDOR_ONBOARDING_CASE_READ: 'vendor_onboarding_case:read',
    VENDOR_ONBOARDING_CASE_WRITE: 'vendor_onboarding_case:write',
    VENDOR_ONBOARDING_CASE_EXPORT: 'vendor_onboarding_case:export',
    VENDOR_ONBOARDING_APPROVAL_READ: 'vendor_onboarding_approval:read',
    VENDOR_ONBOARDING_APPROVAL_WRITE: 'vendor_onboarding_approval:write',
    // Deletion Orchestration (Sprint 2.2.0)
    DELETION_ORCHESTRATION_READ: 'deletion_orchestration:read',
    DELETION_ORCHESTRATION_WRITE: 'deletion_orchestration:write',
    DELETION_ORCHESTRATION_EXECUTE: 'deletion_orchestration:execute',
    DELETION_ORCHESTRATION_VERIFY: 'deletion_orchestration:verify',
    DELETION_ORCHESTRATION_CANCEL: 'deletion_orchestration:cancel',
    DELETION_TEMPLATE_READ: 'deletion_template:read',
    DELETION_TEMPLATE_WRITE: 'deletion_template:write',
    DELETION_VENDOR_REQUEST_READ: 'deletion_vendor_request:read',
    DELETION_VENDOR_REQUEST_WRITE: 'deletion_vendor_request:write',
    DELETION_VENDOR_REQUEST_ESCALATE: 'deletion_vendor_request:escalate',
    DELETION_PROOF_READ: 'deletion_proof:read',
    DELETION_PROOF_CONFIGURE: 'deletion_proof:configure',
    DELETION_REPORT_GENERATE: 'deletion_report:generate',
    // Data Discovery & Classification Automation (Sprint 2.3.0)
    DISCOVERY_READ: 'discovery:read',
    DISCOVERY_WRITE: 'discovery:write',
    DISCOVERY_PROMOTE: 'discovery:promote',
    DISCOVERY_CONFIGURE: 'discovery:configure',
    CLASSIFICATION_SUGGESTION_READ: 'classification_suggestion:read',
    CLASSIFICATION_SUGGESTION_REVIEW: 'classification_suggestion:review',
    CLASSIFICATION_RULE_READ: 'classification_rule:read',
    CLASSIFICATION_RULE_WRITE: 'classification_rule:write',
    DISCOVERY_EXPORT_EXECUTE: 'discovery_export:execute',
    // Classification Maturity (Sprint 20.8.0)
    CLASSIFICATION_REVIEW_READ: 'classification_review:read',
    CLASSIFICATION_REVIEW_WRITE: 'classification_review:write',
    CLASSIFICATION_EXCEPTION_READ: 'classification_exception:read',
    CLASSIFICATION_EXCEPTION_WRITE: 'classification_exception:write',
    CLASSIFICATION_DISCOVERY_READ: 'classification_discovery:read',
    CLASSIFICATION_DISCOVERY_WRITE: 'classification_discovery:write',
    // Incident Operations Level 2 (Sprint 2.4.0)
    //
    // @deprecated — Sprint 44.3.0 B3 — This INCIDENT_* family is the
    // legacy permission namespace. The canonical family is INCIDENT_OPS_*
    // (Sprint 21.2.0, below at lines ~422+). Do NOT add new references
    // to the INCIDENT_* family — consume INCIDENT_OPS_* instead.
    //
    // Migration mapping (clean 1:1):
    //   INCIDENT_PLAYBOOK_READ          -> INCIDENT_OPS_PLAYBOOK_LINK_READ
    //   INCIDENT_PLAYBOOK_WRITE         -> INCIDENT_OPS_PLAYBOOK_LINK_WRITE
    //   INCIDENT_SLA_READ               -> INCIDENT_OPS_REGULATORY_SLA_READ
    //   INCIDENT_SLA_WRITE              -> INCIDENT_OPS_REGULATORY_SLA_WRITE
    //   INCIDENT_REGULATOR_EXPORT_READ  -> INCIDENT_OPS_COORDINATION_READ
    //   INCIDENT_VENDOR_COORD_READ      -> INCIDENT_OPS_COORDINATION_READ
    //   INCIDENT_VENDOR_COORD_WRITE     -> INCIDENT_OPS_COORDINATION_WRITE
    //
    // No INCIDENT_OPS_* equivalent exists yet for:
    //   INCIDENT_PLAYBOOK_APPROVE, INCIDENT_PLAYBOOK_ACTIVATE,
    //   INCIDENT_REGULATOR_EXPORT_EXECUTE, INCIDENT_REGULATOR_EXPORT_SUBMIT,
    //   INCIDENT_METRICS_READ, INCIDENT_METRICS_EXPORT.
    //
    // Full route/page migration deferred to Sprint 44.3.1 (needs new
    // INCIDENT_OPS_*_APPROVE / ACTIVATE / EXECUTE / SUBMIT / METRICS_* perms).
    /** @deprecated Use INCIDENT_OPS_PLAYBOOK_LINK_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_PLAYBOOK_READ: 'incident_playbook:read',
    /** @deprecated Use INCIDENT_OPS_PLAYBOOK_LINK_WRITE instead. Sprint 44.3.0 B3. */
    INCIDENT_PLAYBOOK_WRITE: 'incident_playbook:write',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_APPROVE equivalent. */
    INCIDENT_PLAYBOOK_APPROVE: 'incident_playbook:approve',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_ACTIVATE equivalent. */
    INCIDENT_PLAYBOOK_ACTIVATE: 'incident_playbook:activate',
    /** @deprecated Use INCIDENT_OPS_REGULATORY_SLA_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_SLA_READ: 'incident_sla:read',
    /** @deprecated Use INCIDENT_OPS_REGULATORY_SLA_WRITE instead. Sprint 44.3.0 B3. */
    INCIDENT_SLA_WRITE: 'incident_sla:write',
    /** @deprecated Use INCIDENT_OPS_COORDINATION_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_REGULATOR_EXPORT_READ: 'incident_regulator_export:read',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_EXECUTE equivalent. */
    INCIDENT_REGULATOR_EXPORT_EXECUTE: 'incident_regulator_export:execute',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_SUBMIT equivalent. */
    INCIDENT_REGULATOR_EXPORT_SUBMIT: 'incident_regulator_export:submit',
    /** @deprecated Use INCIDENT_OPS_COORDINATION_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_VENDOR_COORD_READ: 'incident_vendor_coord:read',
    /** @deprecated Use INCIDENT_OPS_COORDINATION_WRITE instead. Sprint 44.3.0 B3. */
    INCIDENT_VENDOR_COORD_WRITE: 'incident_vendor_coord:write',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_METRICS_READ equivalent. */
    INCIDENT_METRICS_READ: 'incident_metrics:read',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_METRICS_EXPORT equivalent. */
    INCIDENT_METRICS_EXPORT: 'incident_metrics:export',
    // Incident Response Maturity (Sprint 21.1.0)
    INCIDENT_BREACH_NOTIFICATION_READ: 'incident_breach_notification:read',
    INCIDENT_BREACH_NOTIFICATION_WRITE: 'incident_breach_notification:write',
    INCIDENT_SUPPLEMENT_READ: 'incident_supplement:read',
    INCIDENT_SUPPLEMENT_WRITE: 'incident_supplement:write',
    INCIDENT_REMEDIATION_READ: 'incident_remediation:read',
    INCIDENT_REMEDIATION_WRITE: 'incident_remediation:write',
    INCIDENT_AUTHORITY_REQUEST_READ: 'incident_authority_request:read',
    INCIDENT_AUTHORITY_REQUEST_WRITE: 'incident_authority_request:write',
    INCIDENT_DISCLOSURE_READ: 'incident_disclosure:read',
    INCIDENT_DISCLOSURE_WRITE: 'incident_disclosure:write',
    // Incident Operations Maturity (Sprint 21.2.0)
    INCIDENT_OPS_PLAYBOOK_LINK_READ: 'incident_ops_playbook_link:read',
    INCIDENT_OPS_PLAYBOOK_LINK_WRITE: 'incident_ops_playbook_link:write',
    INCIDENT_OPS_REGULATORY_SLA_READ: 'incident_ops_regulatory_sla:read',
    INCIDENT_OPS_REGULATORY_SLA_WRITE: 'incident_ops_regulatory_sla:write',
    INCIDENT_OPS_COORDINATION_READ: 'incident_ops_coordination:read',
    INCIDENT_OPS_COORDINATION_WRITE: 'incident_ops_coordination:write',
    INCIDENT_OPS_EVIDENCE_CHAIN_READ: 'incident_ops_evidence_chain:read',
    INCIDENT_OPS_EVIDENCE_CHAIN_WRITE: 'incident_ops_evidence_chain:write',
    INCIDENT_OPS_POST_REVIEW_READ: 'incident_ops_post_review:read',
    INCIDENT_OPS_POST_REVIEW_WRITE: 'incident_ops_post_review:write',
    // Sprint 44.3.1 Epic A — gap-filler perms that unblock the INCIDENT_*
    // legacy family route migration (B3). Each maps to one of the 5
    // legacy perms that had no INCIDENT_OPS_* equivalent.
    INCIDENT_OPS_PLAYBOOK_LINK_APPROVE: 'incident_ops_playbook_link:approve',
    INCIDENT_OPS_PLAYBOOK_LINK_ACTIVATE: 'incident_ops_playbook_link:activate',
    INCIDENT_OPS_COORDINATION_EXECUTE: 'incident_ops_coordination:execute',
    INCIDENT_OPS_COORDINATION_SUBMIT: 'incident_ops_coordination:submit',
    INCIDENT_OPS_METRICS_READ: 'incident_ops_metrics:read',
    INCIDENT_OPS_METRICS_EXPORT: 'incident_ops_metrics:export',
    // Program Governance (Sprint 2.5.0)
    MATURITY_MODEL_READ: 'maturity_model:read',
    MATURITY_MODEL_WRITE: 'maturity_model:write',
    MATURITY_ASSESSMENT_READ: 'maturity_assessment:read',
    MATURITY_ASSESSMENT_WRITE: 'maturity_assessment:write',
    MATURITY_ASSESSMENT_APPROVE: 'maturity_assessment:approve',
    TREND_DASHBOARD_READ: 'trend_dashboard:read',
    // Sprint 43.7.1 Wave 2 — renamed from TREND_DASHBOARD_CONFIGURE. The
    // only consumer left after 43.7.0 removed DashboardConfigPage is the
    // /trends and /trends/batch POST routes, which write trend metrics,
    // not dashboard config. Naming now matches the actual resource.
    TREND_METRIC_WRITE: 'trend_metric:write',
    EXECUTIVE_REPORT_READ: 'executive_report:read',
    EXECUTIVE_REPORT_WRITE: 'executive_report:write',
    EXECUTIVE_REPORT_PUBLISH: 'executive_report:publish',
    AUDIT_READINESS_READ: 'audit_readiness:read',
    AUDIT_READINESS_WRITE: 'audit_readiness:write',
    // Sprint 41.5.0 G5 — Program Governance permission split.
    // The 2026-04-11 module finalization review found that
    // governance-documents, obligation-mappings, raci-assignments,
    // and acknowledgment routes all gated on the blanket
    // MATURITY_MODEL_* permissions. This collapsed the ACL for
    // P-VN-01 (DPO), P-VN-05 (Data Governance Lead), P-VN-10
    // (Internal Audit), P-VN-11 (HR), P-VN-12 (Subsidiary), and
    // P-VN-14 (External Legal) into the same access pattern. The
    // 14 permissions below restore the semantic distinction so
    // each persona can be gated correctly per UC-VN-17 (Governance
    // Model Issuance) and downstream document/acknowledgment flows.
    GOV_DOC_READ: 'gov_doc:read',
    GOV_DOC_WRITE: 'gov_doc:write',
    GOV_DOC_ISSUE: 'gov_doc:issue',
    GOV_DOC_ARCHIVE: 'gov_doc:archive',
    OBLIGATION_MAPPING_READ: 'obligation_mapping:read',
    OBLIGATION_MAPPING_WRITE: 'obligation_mapping:write',
    MATURITY_READ: 'maturity:read',
    MATURITY_WRITE: 'maturity:write',
    MATURITY_ASSESS: 'maturity:assess',
    ACKNOWLEDGMENT_READ: 'acknowledgment:read',
    ACKNOWLEDGMENT_MANAGE: 'acknowledgment:manage',
    CHANGE_LOG_READ: 'change_log:read',
    REVIEW_CADENCE_READ: 'review_cadence:read',
    REVIEW_CADENCE_MANAGE: 'review_cadence:manage',
    GOVERNANCE_EXPORT_EXECUTE: 'governance_export:execute',
    // Access Accountability (Sprint 2.6.0)
    ACCESS_LOG_READ: 'access_accountability:log:read',
    ACCESS_LOG_EXPORT: 'access_accountability:log:export',
    ACCESS_JUSTIFICATION_READ: 'access_accountability:justification:read',
    ACCESS_JUSTIFICATION_REVIEW: 'access_accountability:justification:review',
    ACCESS_INVESTIGATION_READ: 'access_accountability:investigation:read',
    ACCESS_INVESTIGATION_WRITE: 'access_accountability:investigation:write',
    ACCESS_INVESTIGATION_CLOSE: 'access_accountability:investigation:close',
    ACCESS_INVESTIGATION_EXPORT: 'access_accountability:investigation:export',
    ACCESS_CONFIG_READ: 'access_accountability:config:read',
    ACCESS_CONFIG_WRITE: 'access_accountability:config:write',
    ACCESS_ANCHORING_CONFIGURE: 'access_accountability:anchoring:configure',
    ACCESS_ANCHORING_VERIFY: 'access_accountability:anchoring:verify',
    ACCESS_SESSION_READ: 'access_accountability:session:read',
    ACCESS_SESSION_REVOKE: 'access_accountability:session:revoke',
    // Consent Properties / Multi-Property Governance (Sprint 2.7.0)
    CONSENT_PROPERTY_READ: 'consent_property:read',
    CONSENT_PROPERTY_WRITE: 'consent_property:write',
    CONSENT_PROPERTY_DECOMMISSION: 'consent_property:decommission',
    CONSENT_BINDING_READ: 'consent_binding:read',
    CONSENT_BINDING_WRITE: 'consent_binding:write',
    CONSENT_COMPARISON_READ: 'consent_comparison:read',
    CONSENT_COMPARISON_EXECUTE: 'consent_comparison:execute',
    CONSENT_CHANGE_REQUEST_READ: 'consent_change_request:read',
    CONSENT_CHANGE_REQUEST_WRITE: 'consent_change_request:write',
    CONSENT_CHANGE_REQUEST_APPROVE: 'consent_change_request:approve',
    CONSENT_CHANGE_REQUEST_DEPLOY: 'consent_change_request:deploy',
    CONSENT_SNAPSHOT_SCHEDULE_READ: 'consent_snapshot_schedule:read',
    CONSENT_SNAPSHOT_SCHEDULE_WRITE: 'consent_snapshot_schedule:write',
    CONSENT_GOLDEN_TEMPLATE_READ: 'consent_golden_template:read',
    CONSENT_GOLDEN_TEMPLATE_WRITE: 'consent_golden_template:write',
    CONSENT_CROSS_PROPERTY_EXPORT: 'consent_cross_property_export:execute',
    CONSENT_PROPERTY_ANALYTICS_READ: 'consent_property_analytics:read',
    // Consent Properties Operational Completeness (Sprint 28.2.1)
    CONSENT_SENSITIVE_DATA_REVIEW: 'consent_sensitive_data:review',
    // International Packaging and Localization (Sprint 2.8.0)
    MARKET_PACK_READ: 'market_pack:read',
    MARKET_PACK_WRITE: 'market_pack:write',
    MARKET_PACK_MANAGE: 'market_pack:manage',
    LANGUAGE_PACK_READ: 'language_pack:read',
    LANGUAGE_PACK_WRITE: 'language_pack:write',
    LANGUAGE_PACK_MANAGE: 'language_pack:manage',
    // Help-Content Pack Management (Sprint 45.8.0)
    HELP_CONTENT_PACK_MANAGE: 'help_content_pack:manage',
    REGULATORY_FRAMEWORK_READ: 'regulatory_framework:read',
    REGULATORY_FRAMEWORK_WRITE: 'regulatory_framework:write',
    REGULATORY_MAPPING_WRITE: 'regulatory_mapping:write',
    PACK_SUBMISSION_READ: 'pack_submission:read',
    PACK_SUBMISSION_WRITE: 'pack_submission:write',
    PACK_SUBMISSION_REVIEW: 'pack_submission:review',
    LOCALIZATION_EXPORT_EXECUTE: 'localization_export:execute',
    // Partner Marketplace (Sprint 2.9.0)
    PARTNER_ORG_READ: 'partner_org:read',
    PARTNER_ORG_WRITE: 'partner_org:write',
    PARTNER_TENANT_READ: 'partner_tenant:read',
    PARTNER_TENANT_PROVISION: 'partner_tenant:provision',
    PARTNER_TENANT_LIFECYCLE: 'partner_tenant:lifecycle',
    SERVICE_TIER_READ: 'service_tier:read',
    SERVICE_TIER_WRITE: 'service_tier:write',
    DELIVERABLE_READ: 'deliverable:read',
    DELIVERABLE_WRITE: 'deliverable:write',
    DELIVERABLE_TEMPLATE_WRITE: 'deliverable_template:write',
    PARTNER_REPORT_READ: 'partner_report:read',
    PARTNER_REPORT_GENERATE: 'partner_report:generate',
    PARTNER_BRAND_READ: 'partner_brand:read',
    PARTNER_BRAND_WRITE: 'partner_brand:write',
    PARTNER_TICKET_READ: 'partner_ticket:read',
    PARTNER_TICKET_WRITE: 'partner_ticket:write',
    PARTNER_TICKET_ESCALATE: 'partner_ticket:escalate',
    PARTNER_WORKFLOW_READ: 'partner_workflow:read',
    PARTNER_WORKFLOW_WRITE: 'partner_workflow:write',
    PARTNER_WORKFLOW_TEMPLATE_WRITE: 'partner_workflow_template:write',
    PARTNER_AUDIT_READ: 'partner_audit:read',
    PARTNER_EXPORT_EXECUTE: 'partner_export:execute',
    // Sprint 3.6.0: File Storage, Email & External Integrations
    ATTACHMENTS_UPLOAD: 'attachments:upload',
    ATTACHMENTS_DOWNLOAD: 'attachments:download',
    ATTACHMENTS_DELETE: 'attachments:delete',
    PLATFORM_WEBHOOKS_READ: 'platform:webhooks:read',
    PLATFORM_WEBHOOKS_WRITE: 'platform:webhooks:write',
    PLATFORM_DOMAINS_READ: 'platform:domains:read',
    PLATFORM_DOMAINS_WRITE: 'platform:domains:write',
    PLATFORM_EXPORT_SCHEDULES_READ: 'platform:export-schedules:read',
    PLATFORM_EXPORT_SCHEDULES_WRITE: 'platform:export-schedules:write',
    // Privacy Scanner (Sprint 4.1.0)
    PRIVACY_SCANNER_READ: 'privacy_scanner:read',
    PRIVACY_SCANNER_WRITE: 'privacy_scanner:write',
    PRIVACY_SCANNER_EXPORT: 'privacy_scanner:export',
    // Module Configuration (Sprint 4.4.0)
    PLATFORM_MODULE_CONFIG_READ: 'platform:module-config:read',
    PLATFORM_MODULE_CONFIG_WRITE: 'platform:module-config:write',
    // Form Engine (Sprint 7.6.0 — Phase 7)
    FORMS_TEMPLATE_READ: 'forms:template:read',
    FORMS_TEMPLATE_WRITE: 'forms:template:write',
    FORMS_INSTANCE_READ: 'forms:instance:read',
    FORMS_INSTANCE_WRITE: 'forms:instance:write',
    FORMS_INSTANCE_APPROVE: 'forms:instance:approve',
    FORMS_INSTANCE_EXPORT: 'forms:instance:export',
    FORMS_AUDIT_READ: 'forms:audit:read',
    // Sprint 11.5.0: Corporate Group Management (Phase 9)
    GROUP_MANAGE: 'group:manage',
    GROUP_READ: 'group:read',
    GROUP_SHARED_DATA_MANAGE: 'group:shared_data:manage',
    GROUP_SHARED_DATA_READ: 'group:shared_data:read',
    GROUP_AUDIT_READ: 'group:audit:read',
    GROUP_DASHBOARD_READ: 'group:dashboard:read',
    GROUP_USERS_MANAGE: 'group:users:manage',
    GROUP_REPORTING_READ: 'group:reporting:read',
    GROUP_SETTINGS_MANAGE: 'group:settings:manage',
    GROUP_ORG_SWITCH: 'group:org:switch',
    // Phase 9 Extension: Organization Management & Public Intake Config
    ORG_MANAGE_READ: 'org:manage:read',
    ORG_MANAGE_WRITE: 'org:manage:write',
    ORG_MANAGE_DEACTIVATE: 'org:manage:deactivate',
    INTAKE_CONFIG_READ: 'intake_config:read',
    INTAKE_CONFIG_WRITE: 'intake_config:write',
    // Task Management (Sprint 17.0.0 — Phase 15)
    TM_TASK_READ: 'tm:task:read',
    TM_TASK_CREATE: 'tm:task:create',
    TM_TASK_UPDATE: 'tm:task:update',
    TM_TASK_DELETE: 'tm:task:delete',
    TM_TASK_ASSIGN: 'tm:task:assign',
    TM_TASK_COMPLETE: 'tm:task:complete',
    TM_TASK_ESCALATE: 'tm:task:escalate',
    TM_COMMENT_READ: 'tm:comment:read',
    TM_COMMENT_WRITE: 'tm:comment:write',
    TM_CHECKLIST_UPDATE: 'tm:checklist:update',
    TM_CALENDAR_READ: 'tm:calendar:read',
    TM_EXPORT: 'tm:export',
    // Workflow Engine (Sprint 17.1.0 — Phase 15)
    WF_DEFINITION_READ: 'wf:definition:read',
    WF_DEFINITION_WRITE: 'wf:definition:write',
    WF_DEFINITION_DELETE: 'wf:definition:delete',
    WF_DEFINITION_ACTIVATE: 'wf:definition:activate',
    WF_INSTANCE_READ: 'wf:instance:read',
    WF_INSTANCE_TRANSITION: 'wf:instance:transition',
    WF_TEMPLATE_READ: 'wf:template:read',
    WF_TEMPLATE_WRITE: 'wf:template:write',
    // Notifications (Sprint 17.3.0 — Phase 15)
    PLATFORM_NOTIFICATIONS_READ: 'platform:notifications:read',
    PLATFORM_NOTIFICATIONS_MANAGE: 'platform:notifications:manage',
    PLATFORM_NOTIFICATIONS_PREFERENCES: 'platform:notifications:preferences',
    PLATFORM_NOTIFICATIONS_ADMIN: 'platform:notifications:admin',
    // Dashboard (Sprint 29.2.0 — Phase 22)
    DASHBOARD_READ: 'dashboard:read',
    // UI Assist (Sprint 17.8.0 — Phase 15)
    UI_ASSIST_EXECUTE: 'ui_assist:execute',
    UI_ASSIST_VIEW: 'ui_assist:view',
    // Agent Lobby — Multi-Session Coordination (UI Assist extension)
    AGENT_LOBBY_VIEW: 'agent_lobby:view',
    AGENT_LOBBY_MANAGE: 'agent_lobby:manage',
    AGENT_LOBBY_DECIDE: 'agent_lobby:decide',
    // Sprint 18.0.0: License & Package Management
    LICENSE_READ: 'license:read',
    LICENSE_ACTIVATE: 'license:activate',
    LICENSE_DEACTIVATE: 'license:deactivate',
    PACKAGE_READ: 'package:read',
    PACKAGE_UPGRADE: 'package:upgrade',
    PACKAGE_DOWNGRADE: 'package:downgrade',
    // Sprint 40.0.0: Bulk Import
    CONSENT_WORKFLOW_IMPORT: 'consent:workflow:import',
    CASE_IMPORT: 'case:import',
    // Sprint 28.7.1: Multi-Department Report Consolidation (UC-VN-11)
    CONSOLIDATION_CASE_CREATE: 'consolidation:case:create',
    CONSOLIDATION_CASE_READ: 'consolidation:case:read',
    CONSOLIDATION_CASE_UPDATE: 'consolidation:case:update',
    CONSOLIDATION_CASE_DELETE: 'consolidation:case:delete',
    CONSOLIDATION_SECTION_SUBMIT: 'consolidation:section:submit',
    CONSOLIDATION_SECTION_REVIEW: 'consolidation:section:review',
    CONSOLIDATION_APPROVAL_DECIDE: 'consolidation:approval:decide',
    CONSOLIDATION_EXPORT_GENERATE: 'consolidation:export:generate',
    CONSOLIDATION_FLAG_RESOLVE: 'consolidation:flag:resolve',
    // Sprint 40.2.0: Data Subject Profile
    DATA_SUBJECT_CREATE: 'data_subject:create',
    DATA_SUBJECT_READ: 'data_subject:read',
    DATA_SUBJECT_UPDATE: 'data_subject:update',
    DATA_SUBJECT_DELETE: 'data_subject:delete',
    DATA_SUBJECT_LINK: 'data_subject:link',
    // Sprint 43.2.0 batch 1 — Rights Request admin & retention & breach
    // (E1 template admin, E2 dept admin, E3 bulk export, E5 breach flag).
    // Role-tier mapping (per sprint-43.2.0.md):
    //   CASE_TEMPLATE_ADMIN  → PLATFORM_ADMIN + COMPLIANCE_OWNER (DPO)
    //   ORG_DEPARTMENT_ADMIN → PLATFORM_ADMIN only
    //   EXPORT_BULK          → PLATFORM_ADMIN + COMPLIANCE_OWNER + CASE_MANAGER
    //   BREACH_FLAG          → COMPLIANCE_OWNER (DPO) only
    CASE_TEMPLATE_ADMIN: 'case:template_admin',
    ORG_DEPARTMENT_ADMIN: 'org:department_admin',
    EXPORT_BULK: 'export:bulk',
    BREACH_FLAG: 'breach:flag',
    // Sprint 47.4.0 — Immutable Audit Trails Foundation
    // Four permission keys that gate the new Platform → Audit & Proof
    // sub-module. All four are eligible across Light, Standard, and
    // Enterprise tiers per the platform-tier decision recorded in the
    // 47.4.x series; commercial gating is via credit-package purchase,
    // not tier (architecture doc §13). Role-tier mapping (per arch §13):
    //   IMMUTABLE_AUDIT_TRAILS_READ      → PLATFORM_ADMIN + COMPLIANCE_OWNER + AUDITOR
    //   IMMUTABLE_AUDIT_TRAILS_CONFIGURE → PLATFORM_ADMIN + COMPLIANCE_OWNER
    //   IMMUTABLE_AUDIT_TRAILS_EXPORT    → PLATFORM_ADMIN + COMPLIANCE_OWNER + AUDITOR
    //   IMMUTABLE_AUDIT_TRAILS_BILLING   → PLATFORM_ADMIN only
    IMMUTABLE_AUDIT_TRAILS_READ: 'platform:immutable_audit_trails:read',
    IMMUTABLE_AUDIT_TRAILS_CONFIGURE: 'platform:immutable_audit_trails:configure',
    IMMUTABLE_AUDIT_TRAILS_EXPORT: 'platform:immutable_audit_trails:export',
    IMMUTABLE_AUDIT_TRAILS_BILLING: 'platform:immutable_audit_trails:billing',
    // Sprint 47.5.0 — Forseti AI Legal Advisor (foundation)
    // Per family plan §9.1 Q2: ALL permissions are available on ALL TIERS
    // (Light/Standard/Enterprise). Tiers differentiate only on the monthly
    // token bucket (`grc_ai_quota_counters`), never on which permissions
    // exist. Role-level gating still applies (e.g. VIEWER gets only :read).
    // ai_advisor (4 + 3 memory permissions added in Sprint 48.0.0c)
    AI_ADVISOR_CHAT: 'ai_advisor:chat',
    AI_ADVISOR_CHAT_SHARE: 'ai_advisor:chat:share',
    AI_ADVISOR_CHAT_EXPORT: 'ai_advisor:chat:export',
    AI_ADVISOR_ADMIN: 'ai_advisor:admin',
    // Sprint 48.0.0c — memory browse + curate. `:read` reads org memories;
    // `:write` edits / deletes auto-inferred + user-explicit rows; `:curate`
    // is admin-only and gates the "promote to admin_curated" action that
    // locks a memory against retention GC.
    AI_ADVISOR_MEMORY_READ: 'ai_advisor:memory:read',
    AI_ADVISOR_MEMORY_WRITE: 'ai_advisor:memory:write',
    AI_ADVISOR_MEMORY_CURATE: 'ai_advisor:memory:curate',
    // ai_projects (5)
    AI_PROJECTS_READ: 'ai_projects:read',
    AI_PROJECTS_WRITE: 'ai_projects:write',
    AI_PROJECTS_SHARE: 'ai_projects:share',
    AI_PROJECTS_DELETE: 'ai_projects:delete',
    AI_PROJECTS_ADMIN: 'ai_projects:admin',
    // ai_workflows (8)
    AI_WORKFLOWS_READ: 'ai_workflows:read',
    AI_WORKFLOWS_WRITE: 'ai_workflows:write',
    AI_WORKFLOWS_RUN: 'ai_workflows:run',
    AI_WORKFLOWS_SHARE: 'ai_workflows:share',
    AI_WORKFLOWS_DELETE: 'ai_workflows:delete',
    AI_WORKFLOWS_ADMIN: 'ai_workflows:admin',
    AI_WORKFLOWS_SYSTEM_PACK_READ: 'ai_workflows:system_pack:read',
    AI_WORKFLOWS_SYSTEM_PACK_HIDE: 'ai_workflows:system_pack:hide',
    // ai_tabular_reviews (6)
    AI_TABULAR_REVIEWS_READ: 'ai_tabular_reviews:read',
    AI_TABULAR_REVIEWS_WRITE: 'ai_tabular_reviews:write',
    AI_TABULAR_REVIEWS_RUN: 'ai_tabular_reviews:run',
    AI_TABULAR_REVIEWS_SHARE: 'ai_tabular_reviews:share',
    AI_TABULAR_REVIEWS_DELETE: 'ai_tabular_reviews:delete',
    AI_TABULAR_REVIEWS_ADMIN: 'ai_tabular_reviews:admin',
    // ai_document_review (5)
    AI_DOCUMENT_REVIEW_PROPOSE_EDIT: 'ai_document_review:propose_edit',
    AI_DOCUMENT_REVIEW_ACCEPT_EDIT: 'ai_document_review:accept_edit',
    AI_DOCUMENT_REVIEW_REJECT_EDIT: 'ai_document_review:reject_edit',
    AI_DOCUMENT_REVIEW_VERSION_PROMOTE: 'ai_document_review:version_promote',
    AI_DOCUMENT_REVIEW_ADMIN: 'ai_document_review:admin',
    // ai_provider (4)
    AI_PROVIDER_CONFIGURE: 'ai_provider:configure',
    AI_PROVIDER_VIEW_USAGE: 'ai_provider:view_usage',
    AI_PROVIDER_VIEW_AUDIT: 'ai_provider:view_audit',
    // Quota override exists in the schema on every tier per family plan §4;
    // Org Administrator role is auto-assigned only on Enterprise. Standard
    // and Light orgs can grant manually if desired.
    AI_PROVIDER_QUOTA_OVERRIDE: 'ai_provider:quota:override',
    // Sprint 48.5.x — self-service "Buy More" billing. Gates both the Stripe
    // checkout-intent proxy (Flow A) and the invoice redeem that credits the
    // token bucket (Flow B). Distinct from `:quota:override` (which lets a
    // user bypass an exhausted bucket without paying); billing lets a user
    // legitimately purchase + redeem additional units. Available on every tier.
    AI_PROVIDER_BILLING: 'ai_provider:billing',
    // Sprint 47.9.7 — MCP connector admin (issuance + rotation + revocation
    // of API keys that external Claude clients use to call Forseti tools).
    // Admin-only — these grant access to the credential surface, separate
    // from the read-only :list which exposes (hashed) key inventory only.
    AI_PROVIDER_MCP_CREATE: 'ai_provider:mcp:create',
    AI_PROVIDER_MCP_REVOKE: 'ai_provider:mcp:revoke',
    AI_PROVIDER_MCP_LIST: 'ai_provider:mcp:list',
    // F19 (fix-sprint-20260503-2128) — evidence review/approve permissions
    // backing `evidence-routes.ts`. Previously the routes used hardcoded
    // string sets (`'evidence:review'`, `'evidence:approve'`) that the
    // permission registry never saw; codifying them on the enum lets the
    // route layer use `requirePermission(...)` middleware and lets the
    // permission-coverage tooling include them in audits.
    EVIDENCE_REVIEW: 'evidence:review',
    EVIDENCE_APPROVE: 'evidence:approve',
};
/**
 * System role definitions.
 * Sprint 1.2.0 adds Contributor and Viewer roles.
 */
export const SystemRole = {
    PLATFORM_ADMIN: 'platform_admin',
    COMPLIANCE_OWNER: 'compliance_owner',
    CASE_MANAGER: 'case_manager',
    AUDITOR: 'auditor',
    SUPPORT: 'support',
    // Sprint 1.2.0 new roles
    CONTRIBUTOR: 'contributor',
    VIEWER: 'viewer',
};
/** All data-mapping read permissions (shared across Viewer+) */
const DM_READ_PERMS = [
    Permission.DM_SYSTEM_READ,
    Permission.DM_CATEGORY_READ,
    Permission.DM_PURPOSE_READ,
    Permission.DM_RETENTION_READ,
    Permission.DM_FLOW_READ,
    Permission.DM_ACTIVITY_READ,
    Permission.DM_ROPA_READ,
    Permission.DM_EVIDENCE_READ,
    Permission.DM_ATTESTATION_READ,
    Permission.DM_OWNERSHIP_READ,
    Permission.DM_RISK_ASSESSMENT_READ,
    Permission.DM_REVIEW_READ,
    Permission.DM_INVENTORY_READ,
    Permission.DM_EXCEPTION_READ,
    Permission.DM_HISTORY_READ,
    // Sprint 45.4.0 — Data Registry read permission shared across Viewer+.
    Permission.DM_REGISTRY_READ,
];
/** Contributor-level write permissions (create/edit, no archive/delete/export) */
const DM_CONTRIBUTOR_PERMS = [
    ...DM_READ_PERMS,
    Permission.DM_SYSTEM_WRITE,
    Permission.DM_FLOW_WRITE,
    Permission.DM_ACTIVITY_WRITE,
    Permission.DM_EVIDENCE_WRITE,
    Permission.DM_EVIDENCE_LINK,
    // Sprint 45.4.0 — contributors (data_custodian-tier) may write + export
    // their org's Data Registry items. Archive + form import are manager-tier.
    Permission.DM_REGISTRY_WRITE,
    Permission.DM_REGISTRY_EXPORT,
];
/** Privacy Manager-level permissions (full CRUD + CSV export + archive) */
const DM_MANAGER_PERMS = [
    ...DM_CONTRIBUTOR_PERMS,
    Permission.DM_CATEGORY_WRITE,
    Permission.DM_PURPOSE_WRITE,
    Permission.DM_RETENTION_WRITE,
    Permission.DM_SYSTEM_ARCHIVE,
    Permission.DM_FLOW_ARCHIVE,
    Permission.DM_ACTIVITY_ARCHIVE,
    Permission.DM_ROPA_GENERATE,
    Permission.DM_EVIDENCE_UNLINK,
    Permission.DM_EXPORT_CSV,
    Permission.DM_AUDIT_READ,
    Permission.DM_ATTESTATION_WRITE,
    Permission.DM_OWNERSHIP_WRITE,
    Permission.DM_RISK_ASSESSMENT_WRITE,
    Permission.DM_RISK_ASSESSMENT_APPROVE,
    Permission.DM_ATTESTATION_EXPORT,
    Permission.DM_REVIEW_WRITE,
    Permission.DM_EXCEPTION_WRITE,
    Permission.DM_CATEGORY_APPROVE,
    // Sprint 45.4.0 — manager-tier Registry ops (archive + form-driven import).
    Permission.DM_REGISTRY_ARCHIVE,
    Permission.DM_REGISTRY_IMPORT_FORM,
];
/** Owner/Admin-level permissions (everything including PDF export + defaults) */
const DM_ADMIN_PERMS = [
    ...DM_MANAGER_PERMS,
    Permission.DM_EVIDENCE_DELETE,
    Permission.DM_EXPORT_PDF,
    // Sprint 44.3.1 Epic C — DM_DEFAULTS_MANAGE removed (deleted perm).
];
/** All governance read permissions (shared across Viewer+) */
const GOV_READ_PERMS = [
    Permission.GOV_VENDOR_READ,
    Permission.GOV_SUBPROCESSOR_READ,
    Permission.GOV_TRANSFER_READ,
    Permission.GOV_CROSSBORDER_READ,
    Permission.GOV_DRIFT_READ,
    Permission.GOV_SCORE_READ,
    Permission.GOV_EVIDENCE_READ,
    Permission.GOV_ONBOARDING_READ,
    Permission.GOV_DPA_REVIEW_READ,
    Permission.GOV_PERIODIC_REVIEW_READ,
    Permission.GOV_OFFBOARDING_READ,
    // Sprint 41.5.0 G1 — Cross-Border Filing read goes to every read tier
    // (DPO, auditor, CEO) so the filings list is visible to all governance
    // viewers. Write/submit/archive land in MANAGER and ADMIN below.
    Permission.GOV_CROSS_BORDER_FILING_READ,
    // Sprint 41.5.0 G10 persona fix — PDF export is a read operation (auditors
    // need it for UC-VN-09 evidence bundle export). Moved from ADMIN to READ.
    Permission.GOV_EXPORT_PDF,
];
/** Contributor-level governance: read + execute assigned steps + upload evidence */
const GOV_CONTRIBUTOR_PERMS = [
    ...GOV_READ_PERMS,
    Permission.GOV_EXECUTION_STEP,
    Permission.GOV_EVIDENCE_WRITE,
    Permission.GOV_EVIDENCE_LINK,
];
/** Privacy Manager-level governance: full CRUD + workflows + CSV export */
const GOV_MANAGER_PERMS = [
    ...GOV_CONTRIBUTOR_PERMS,
    Permission.GOV_VENDOR_WRITE,
    Permission.GOV_VENDOR_OFFBOARD,
    Permission.GOV_SUBPROCESSOR_WRITE,
    Permission.GOV_TRANSFER_WRITE,
    Permission.GOV_DRIFT_MANAGE,
    Permission.GOV_DRIFT_ACCEPT_RISK,
    Permission.GOV_SCORE_WRITE,
    Permission.GOV_PLAYBOOK_READ,
    Permission.GOV_PLAYBOOK_WRITE,
    Permission.GOV_EXECUTION_READ,
    Permission.GOV_EXECUTION_INITIATE,
    Permission.GOV_APPROVAL_READ,
    Permission.GOV_AUDIT_READ,
    Permission.GOV_EXPORT_CSV,
    Permission.GOV_ONBOARDING_WRITE,
    Permission.GOV_DPA_REVIEW_WRITE,
    Permission.GOV_PERIODIC_REVIEW_WRITE,
    Permission.GOV_OFFBOARDING_WRITE,
    // Sprint 41.5.0 G1 — Cross-Border Filing manager-tier verbs
    Permission.GOV_CROSS_BORDER_FILING_WRITE,
    Permission.GOV_CROSS_BORDER_FILING_SUBMIT,
];
/** Owner/Admin governance: everything + approvals + PDF export + delete */
const GOV_ADMIN_PERMS = [
    ...GOV_MANAGER_PERMS,
    Permission.GOV_VENDOR_APPROVE,
    Permission.GOV_PLAYBOOK_DELETE,
    Permission.GOV_APPROVAL_DECIDE,
    Permission.GOV_EVIDENCE_DELETE,
    Permission.GOV_EXPORT_EXECUTE,
    // Sprint 41.5.0 G1 — Archive is admin-only (DPO + admin can archive
    // a filing once it's terminal-acknowledged)
    Permission.GOV_CROSS_BORDER_FILING_ARCHIVE,
];
/** Consent read permissions (shared across Auditor+) */
const CONSENT_READ_PERMS = [
    Permission.CONSENT_POLICY_READ,
    Permission.CONSENT_WORKFLOW_READ,
    Permission.CONSENT_CMP_SNAPSHOT_READ,
    Permission.CONSENT_PROPAGATION_READ,
    Permission.CONSENT_RECONCILIATION_READ,
    Permission.CONSENT_SYNC_READ,
];
/** Consent manager-level: read + write policies and workflows + export */
const CONSENT_MANAGER_PERMS = [
    ...CONSENT_READ_PERMS,
    Permission.CONSENT_POLICY_WRITE,
    Permission.CONSENT_WORKFLOW_WRITE,
    Permission.CONSENT_EXPORT_EXECUTE,
    Permission.CONSENT_PROPAGATION_WRITE,
    Permission.CONSENT_RECONCILIATION_WRITE,
    Permission.CONSENT_SYNC_WRITE,
];
/** Consent admin: everything including approve, delete, CMP snapshot write, and import */
const CONSENT_ADMIN_PERMS = [
    ...CONSENT_MANAGER_PERMS,
    Permission.CONSENT_POLICY_APPROVE,
    Permission.CONSENT_POLICY_DELETE,
    Permission.CONSENT_CMP_SNAPSHOT_WRITE,
    Permission.CONSENT_WORKFLOW_IMPORT,
];
/** Assessment read permissions (Sprint 1.5.0) */
const ASSESS_READ_PERMS = [
    Permission.DPIA_READ,
    Permission.TRANSFER_ASSESSMENT_READ,
];
/** Assessment manager: read + write + export */
const ASSESS_MANAGER_PERMS = [
    ...ASSESS_READ_PERMS,
    Permission.DPIA_WRITE,
    Permission.TRANSFER_ASSESSMENT_WRITE,
    // Sprint 44.3.1 Epic C — ASSESSMENT_EXPORT_EXECUTE removed (deleted perm).
];
/** Assessment admin: everything including approve + delete */
const ASSESS_ADMIN_PERMS = [
    ...ASSESS_MANAGER_PERMS,
    Permission.DPIA_APPROVE,
    Permission.DPIA_DELETE,
    Permission.TRANSFER_ASSESSMENT_APPROVE,
    Permission.TRANSFER_ASSESSMENT_DELETE,
    Permission.ASSESSMENT_TRIGGER_CONFIGURE,
    // fix-sprint-20260619-0053-followup-2 #10: PDPL evidence-pack transmission
    // is org_admin/DPO-scoped by default. Broader compliance staff can still
    // draft + edit + upload proof via the unchanged DPIA_WRITE surface.
    Permission.PDPL_EVIDENCE_PACK_SEND,
];
/** Classification read permissions (Sprint 1.6.0 + 20.8.0) */
const CLASS_READ_PERMS = [
    Permission.TAXONOMY_READ,
    Permission.CLASSIFICATION_READ,
    Permission.LIFECYCLE_READ,
    Permission.GAP_REPORT_READ,
    // Sprint 20.8.0: maturity read
    Permission.CLASSIFICATION_REVIEW_READ,
    Permission.CLASSIFICATION_EXCEPTION_READ,
    Permission.CLASSIFICATION_DISCOVERY_READ,
];
/** Classification manager: read + write + review + export */
const CLASS_MANAGER_PERMS = [
    ...CLASS_READ_PERMS,
    Permission.TAXONOMY_WRITE,
    Permission.CLASSIFICATION_WRITE,
    Permission.CLASSIFICATION_REVIEW,
    Permission.LIFECYCLE_WRITE,
    Permission.GAP_REPORT_MANAGE,
    Permission.CLASSIFICATION_EXPORT_EXECUTE,
    // Sprint 20.8.0: maturity write
    Permission.CLASSIFICATION_REVIEW_WRITE,
    Permission.CLASSIFICATION_EXCEPTION_WRITE,
    Permission.CLASSIFICATION_DISCOVERY_WRITE,
];
/** Classification admin: everything including taxonomy delete + lifecycle hold */
const CLASS_ADMIN_PERMS = [
    ...CLASS_MANAGER_PERMS,
    Permission.TAXONOMY_DELETE,
    Permission.LIFECYCLE_HOLD,
];
/** Monitoring read permissions (Sprint 1.7.0) */
const MON_READ_PERMS = [
    Permission.PROGRAM_READ,
    Permission.REVIEW_TASK_READ,
    Permission.TASK_TEMPLATE_READ,
    Permission.KPI_READ,
    Permission.RIC_READ,
    // Sprint 21.3.0: Monitoring Programs Maturity
    Permission.MONITORING_FRAMEWORK_LINK_READ,
    Permission.MONITORING_AUTHORITY_READINESS_READ,
    Permission.MONITORING_CHANGE_REVIEW_READ,
    // Sprint 27.7.1: Monitoring Operational Completeness
    Permission.MONITORING_RACI_READ,
];
/** Monitoring manager: read + write programs/tasks/templates + export */
const MON_MANAGER_PERMS = [
    ...MON_READ_PERMS,
    Permission.PROGRAM_WRITE,
    Permission.REVIEW_TASK_WRITE,
    Permission.REVIEW_TASK_ASSIGN,
    Permission.TASK_TEMPLATE_WRITE,
    Permission.KPI_WRITE,
    Permission.RIC_WRITE,
    // Sprint 44.3.1 Epic C — PROGRAM_EXPORT_EXECUTE removed (deleted perm;
    // superseded by monitoring_export:execute below).
    // Sprint 21.3.0: Monitoring Programs Maturity
    Permission.MONITORING_FRAMEWORK_LINK_WRITE,
    Permission.MONITORING_AUTHORITY_READINESS_WRITE,
    Permission.MONITORING_CHANGE_REVIEW_WRITE,
    // Sprint 27.7.1: Monitoring Operational Completeness
    Permission.MONITORING_EXPORT,
    Permission.MONITORING_RACI_WRITE,
];
/** Monitoring admin: everything including program manage */
const MON_ADMIN_PERMS = [...MON_MANAGER_PERMS, Permission.PROGRAM_MANAGE];
/** Incident read permissions (Sprint 1.8.0) */
const INC_READ_PERMS = [
    Permission.INCIDENT_READ,
    Permission.INCIDENT_TASK_READ,
];
/** Incident responder: read + write incidents/tasks */
const INC_RESPONDER_PERMS = [
    ...INC_READ_PERMS,
    Permission.INCIDENT_WRITE,
    Permission.INCIDENT_TASK_WRITE,
];
/** Incident manager: responder + triage + assess + assign + decisions + export */
const INC_MANAGER_PERMS = [
    ...INC_RESPONDER_PERMS,
    Permission.INCIDENT_TRIAGE,
    Permission.INCIDENT_ASSESS,
    Permission.INCIDENT_TASK_ASSIGN,
    Permission.INCIDENT_DECISION_WRITE,
    Permission.INCIDENT_EXPORT_EXECUTE,
];
/** Incident admin: everything including close + notification manage */
const INC_ADMIN_PERMS = [
    ...INC_MANAGER_PERMS,
    Permission.INCIDENT_CLOSE,
    Permission.INCIDENT_NOTIFICATION_MANAGE,
];
/** Connector read permissions (Sprint 2.0.0) */
const CONN_READ_PERMS = [
    Permission.CONNECTOR_READ,
    Permission.EVENT_BUS_READ,
];
/** Connector manager: read + test + credential read */
const CONN_MANAGER_PERMS = [
    ...CONN_READ_PERMS,
    Permission.CONNECTOR_TEST,
    Permission.CREDENTIAL_READ,
];
/** Connector admin: everything including write, manage, credential write, SSO, storage */
const CONN_ADMIN_PERMS = [
    ...CONN_MANAGER_PERMS,
    Permission.CONNECTOR_WRITE,
    Permission.CONNECTOR_MANAGE,
    Permission.CREDENTIAL_WRITE,
    Permission.EVENT_BUS_MANAGE,
    Permission.IDENTITY_SSO_CONFIGURE,
    Permission.STORAGE_CONNECTOR_CONFIGURE,
];
/** Vendor risk read permissions (Sprint 2.1.0) */
const VR_READ_PERMS = [
    Permission.VENDOR_RISK_CONFIG_READ,
    Permission.VENDOR_RISK_SCORE_READ,
    Permission.VENDOR_QUESTIONNAIRE_TEMPLATE_READ,
    Permission.VENDOR_QUESTIONNAIRE_READ,
    Permission.VENDOR_CONTRACT_READ,
    Permission.VENDOR_REVIEW_READ,
    Permission.VENDOR_RISK_SIGNAL_READ,
    Permission.VENDOR_RISK_REASSESSMENT_READ,
    Permission.VENDOR_RISK_ACCEPTANCE_READ,
    Permission.VENDOR_RISK_PORTFOLIO_READ,
    Permission.VENDOR_RISK_REVIEW_ROUTING_READ,
    Permission.VENDOR_ONBOARDING_CASE_READ,
    Permission.VENDOR_ONBOARDING_APPROVAL_READ,
];
/** Vendor risk manager: read + score + questionnaire write + contract write + review + export */
const VR_MANAGER_PERMS = [
    ...VR_READ_PERMS,
    Permission.VENDOR_RISK_SCORE_WRITE,
    Permission.VENDOR_QUESTIONNAIRE_WRITE,
    Permission.VENDOR_CONTRACT_WRITE,
    Permission.VENDOR_GATE_EVALUATE,
    Permission.VENDOR_REVIEW_WRITE,
    Permission.VENDOR_RISK_EXPORT_EXECUTE,
    Permission.VENDOR_RISK_SIGNAL_WRITE,
    Permission.VENDOR_RISK_REASSESSMENT_WRITE,
    Permission.VENDOR_RISK_ACCEPTANCE_WRITE,
    Permission.VENDOR_RISK_REVIEW_ROUTING_WRITE,
    Permission.VENDOR_ONBOARDING_CASE_WRITE,
    Permission.VENDOR_ONBOARDING_CASE_EXPORT,
    Permission.VENDOR_ONBOARDING_APPROVAL_WRITE,
];
/** Vendor risk admin: everything including config write, template write, questionnaire review, gate override */
const VR_ADMIN_PERMS = [
    ...VR_MANAGER_PERMS,
    Permission.VENDOR_RISK_CONFIG_WRITE,
    Permission.VENDOR_QUESTIONNAIRE_TEMPLATE_WRITE,
    Permission.VENDOR_QUESTIONNAIRE_REVIEW,
    Permission.VENDOR_GATE_OVERRIDE,
];
/** Deletion orchestration read permissions (Sprint 2.2.0) */
const DELORCH_READ_PERMS = [
    Permission.DELETION_ORCHESTRATION_READ,
    Permission.DELETION_TEMPLATE_READ,
    Permission.DELETION_VENDOR_REQUEST_READ,
    Permission.DELETION_PROOF_READ,
];
/** Deletion orchestration manager: read + write + execute + escalate + report */
const DELORCH_MANAGER_PERMS = [
    ...DELORCH_READ_PERMS,
    Permission.DELETION_ORCHESTRATION_WRITE,
    Permission.DELETION_ORCHESTRATION_EXECUTE,
    Permission.DELETION_TEMPLATE_WRITE,
    Permission.DELETION_VENDOR_REQUEST_WRITE,
    Permission.DELETION_VENDOR_REQUEST_ESCALATE,
    Permission.DELETION_REPORT_GENERATE,
];
/** Deletion orchestration admin: everything including verify, cancel, proof configure */
const DELORCH_ADMIN_PERMS = [
    ...DELORCH_MANAGER_PERMS,
    Permission.DELETION_ORCHESTRATION_VERIFY,
    Permission.DELETION_ORCHESTRATION_CANCEL,
    Permission.DELETION_PROOF_CONFIGURE,
];
/** Discovery read permissions (Sprint 2.3.0) */
const DISC_READ_PERMS = [
    Permission.DISCOVERY_READ,
    Permission.CLASSIFICATION_SUGGESTION_READ,
    Permission.CLASSIFICATION_RULE_READ,
];
/** Discovery manager: read + write + promote + suggestion review + rule write + export */
const DISC_MANAGER_PERMS = [
    ...DISC_READ_PERMS,
    Permission.DISCOVERY_WRITE,
    Permission.DISCOVERY_PROMOTE,
    Permission.CLASSIFICATION_SUGGESTION_REVIEW,
    Permission.CLASSIFICATION_RULE_WRITE,
    Permission.DISCOVERY_EXPORT_EXECUTE,
];
/** Discovery admin: everything including configure */
const DISC_ADMIN_PERMS = [
    ...DISC_MANAGER_PERMS,
    Permission.DISCOVERY_CONFIGURE,
];
/** Incident ops read permissions (Sprint 2.4.0) */
const INCOPS_READ_PERMS = [
    Permission.INCIDENT_PLAYBOOK_READ,
    Permission.INCIDENT_SLA_READ,
    Permission.INCIDENT_REGULATOR_EXPORT_READ,
    Permission.INCIDENT_VENDOR_COORD_READ,
    Permission.INCIDENT_METRICS_READ,
];
/** Incident ops manager: read + write playbooks + activate + vendor coord + regulator export */
const INCOPS_MANAGER_PERMS = [
    ...INCOPS_READ_PERMS,
    Permission.INCIDENT_PLAYBOOK_WRITE,
    Permission.INCIDENT_PLAYBOOK_ACTIVATE,
    Permission.INCIDENT_VENDOR_COORD_WRITE,
    Permission.INCIDENT_REGULATOR_EXPORT_EXECUTE,
    Permission.INCIDENT_METRICS_EXPORT,
];
/** Incident ops admin: everything including approve, SLA write, regulator submit */
const INCOPS_ADMIN_PERMS = [
    ...INCOPS_MANAGER_PERMS,
    Permission.INCIDENT_PLAYBOOK_APPROVE,
    Permission.INCIDENT_SLA_WRITE,
    Permission.INCIDENT_REGULATOR_EXPORT_SUBMIT,
];
/** Incident response maturity read permissions (Sprint 21.1.0) */
export const incidentBreachNotificationReadTier = [
    Permission.INCIDENT_BREACH_NOTIFICATION_READ,
    Permission.INCIDENT_SUPPLEMENT_READ,
    Permission.INCIDENT_REMEDIATION_READ,
    Permission.INCIDENT_AUTHORITY_REQUEST_READ,
    Permission.INCIDENT_DISCLOSURE_READ,
];
/** Incident response maturity write permissions (Sprint 21.1.0) */
export const incidentBreachNotificationWriteTier = [
    Permission.INCIDENT_BREACH_NOTIFICATION_WRITE,
    Permission.INCIDENT_SUPPLEMENT_WRITE,
    Permission.INCIDENT_REMEDIATION_WRITE,
    Permission.INCIDENT_AUTHORITY_REQUEST_WRITE,
    Permission.INCIDENT_DISCLOSURE_WRITE,
];
/** Incident ops maturity read permissions (Sprint 21.2.0) */
export const incidentOpsMaturityReadTier = [
    Permission.INCIDENT_OPS_PLAYBOOK_LINK_READ,
    Permission.INCIDENT_OPS_REGULATORY_SLA_READ,
    Permission.INCIDENT_OPS_COORDINATION_READ,
    Permission.INCIDENT_OPS_EVIDENCE_CHAIN_READ,
    Permission.INCIDENT_OPS_POST_REVIEW_READ,
];
/** Incident ops maturity write permissions (Sprint 21.2.0) */
export const incidentOpsMaturityWriteTier = [
    Permission.INCIDENT_OPS_PLAYBOOK_LINK_WRITE,
    Permission.INCIDENT_OPS_REGULATORY_SLA_WRITE,
    Permission.INCIDENT_OPS_COORDINATION_WRITE,
    Permission.INCIDENT_OPS_EVIDENCE_CHAIN_WRITE,
    Permission.INCIDENT_OPS_POST_REVIEW_WRITE,
];
/** Program governance read permissions (Sprint 2.5.0; expanded by 41.5.0 G5) */
const PGOV_READ_PERMS = [
    Permission.MATURITY_MODEL_READ,
    Permission.MATURITY_ASSESSMENT_READ,
    Permission.TREND_DASHBOARD_READ,
    Permission.EXECUTIVE_REPORT_READ,
    Permission.AUDIT_READINESS_READ,
    // Sprint 41.5.0 G5 — new read permissions split out from MATURITY_MODEL_READ
    Permission.GOV_DOC_READ,
    Permission.OBLIGATION_MAPPING_READ,
    Permission.MATURITY_READ,
    Permission.ACKNOWLEDGMENT_READ,
    Permission.CHANGE_LOG_READ,
    Permission.REVIEW_CADENCE_READ,
];
/** Program governance manager: read + write + configure + export */
const PGOV_MANAGER_PERMS = [
    ...PGOV_READ_PERMS,
    Permission.MATURITY_MODEL_WRITE,
    Permission.MATURITY_ASSESSMENT_WRITE,
    Permission.TREND_METRIC_WRITE,
    Permission.EXECUTIVE_REPORT_WRITE,
    Permission.AUDIT_READINESS_WRITE,
    Permission.GOVERNANCE_EXPORT_EXECUTE,
    // Sprint 41.5.0 G5 — manager-tier write permissions
    Permission.GOV_DOC_WRITE,
    Permission.OBLIGATION_MAPPING_WRITE,
    Permission.MATURITY_WRITE,
    Permission.ACKNOWLEDGMENT_MANAGE,
    Permission.REVIEW_CADENCE_MANAGE,
];
/** Program governance admin: everything including approve + publish + issue + archive */
const PGOV_ADMIN_PERMS = [
    ...PGOV_MANAGER_PERMS,
    Permission.MATURITY_ASSESSMENT_APPROVE,
    Permission.EXECUTIVE_REPORT_PUBLISH,
    // Sprint 41.5.0 G5 — admin-tier governance document lifecycle controls
    Permission.GOV_DOC_ISSUE,
    Permission.GOV_DOC_ARCHIVE,
    Permission.MATURITY_ASSESS,
];
/** Access accountability read permissions (Sprint 2.6.0) */
const ACCT_READ_PERMS = [
    Permission.ACCESS_LOG_READ,
    Permission.ACCESS_JUSTIFICATION_READ,
    Permission.ACCESS_INVESTIGATION_READ,
    Permission.ACCESS_CONFIG_READ,
    Permission.ACCESS_ANCHORING_VERIFY,
    Permission.ACCESS_SESSION_READ,
];
/** Access accountability manager: read + review + write + export + close */
const ACCT_MANAGER_PERMS = [
    ...ACCT_READ_PERMS,
    Permission.ACCESS_LOG_EXPORT,
    Permission.ACCESS_JUSTIFICATION_REVIEW,
    Permission.ACCESS_INVESTIGATION_WRITE,
    Permission.ACCESS_INVESTIGATION_EXPORT,
];
/** Access accountability admin: everything including close, config write, anchoring configure, session revoke */
const ACCT_ADMIN_PERMS = [
    ...ACCT_MANAGER_PERMS,
    Permission.ACCESS_INVESTIGATION_CLOSE,
    Permission.ACCESS_CONFIG_WRITE,
    Permission.ACCESS_ANCHORING_CONFIGURE,
    Permission.ACCESS_SESSION_REVOKE,
];
/** Consent property read permissions (Sprint 2.7.0) */
const CPROP_READ_PERMS = [
    Permission.CONSENT_PROPERTY_READ,
    Permission.CONSENT_BINDING_READ,
    Permission.CONSENT_COMPARISON_READ,
    Permission.CONSENT_CHANGE_REQUEST_READ,
    Permission.CONSENT_SNAPSHOT_SCHEDULE_READ,
    Permission.CONSENT_GOLDEN_TEMPLATE_READ,
    Permission.CONSENT_PROPERTY_ANALYTICS_READ,
];
/** Consent property manager: read + write + execute comparisons + export */
const CPROP_MANAGER_PERMS = [
    ...CPROP_READ_PERMS,
    Permission.CONSENT_PROPERTY_WRITE,
    Permission.CONSENT_BINDING_WRITE,
    Permission.CONSENT_COMPARISON_EXECUTE,
    Permission.CONSENT_CHANGE_REQUEST_WRITE,
    Permission.CONSENT_CROSS_PROPERTY_EXPORT,
];
/** Consent property admin: everything including decommission, approve, deploy, schedule write, golden template write */
const CPROP_ADMIN_PERMS = [
    ...CPROP_MANAGER_PERMS,
    Permission.CONSENT_PROPERTY_DECOMMISSION,
    Permission.CONSENT_CHANGE_REQUEST_APPROVE,
    Permission.CONSENT_CHANGE_REQUEST_DEPLOY,
    Permission.CONSENT_SNAPSHOT_SCHEDULE_WRITE,
    Permission.CONSENT_GOLDEN_TEMPLATE_WRITE,
    Permission.CONSENT_SENSITIVE_DATA_REVIEW,
];
/** Localization read permissions (Sprint 2.8.0) */
const L10N_READ_PERMS = [
    Permission.MARKET_PACK_READ,
    Permission.LANGUAGE_PACK_READ,
    Permission.REGULATORY_FRAMEWORK_READ,
    Permission.PACK_SUBMISSION_READ,
];
/** Localization manager: read + install packs + write language packs + write frameworks + write mappings + submit + export */
const L10N_MANAGER_PERMS = [
    ...L10N_READ_PERMS,
    Permission.MARKET_PACK_WRITE,
    Permission.LANGUAGE_PACK_WRITE,
    Permission.REGULATORY_FRAMEWORK_WRITE,
    Permission.REGULATORY_MAPPING_WRITE,
    Permission.PACK_SUBMISSION_WRITE,
    Permission.LOCALIZATION_EXPORT_EXECUTE,
];
/** Localization admin: everything including manage packs, manage language packs, review submissions */
const L10N_ADMIN_PERMS = [
    ...L10N_MANAGER_PERMS,
    Permission.MARKET_PACK_MANAGE,
    Permission.LANGUAGE_PACK_MANAGE,
    Permission.HELP_CONTENT_PACK_MANAGE,
    Permission.PACK_SUBMISSION_REVIEW,
];
/** Partner marketplace read permissions (Sprint 2.9.0) */
const PM_READ_PERMS = [
    Permission.PARTNER_ORG_READ,
    Permission.PARTNER_TENANT_READ,
    Permission.SERVICE_TIER_READ,
    Permission.DELIVERABLE_READ,
    Permission.PARTNER_REPORT_READ,
    Permission.PARTNER_BRAND_READ,
    Permission.PARTNER_TICKET_READ,
    Permission.PARTNER_WORKFLOW_READ,
];
/** Partner marketplace manager: read + provision + write deliverables/tickets/workflows + generate reports + escalate */
const PM_MANAGER_PERMS = [
    ...PM_READ_PERMS,
    Permission.PARTNER_TENANT_PROVISION,
    Permission.DELIVERABLE_WRITE,
    Permission.PARTNER_REPORT_GENERATE,
    Permission.PARTNER_TICKET_WRITE,
    Permission.PARTNER_TICKET_ESCALATE,
    Permission.PARTNER_WORKFLOW_WRITE,
];
/** Partner marketplace admin: everything including org write, lifecycle, tier write, template write, brand write, audit, export */
const PM_ADMIN_PERMS = [
    ...PM_MANAGER_PERMS,
    Permission.PARTNER_ORG_WRITE,
    Permission.PARTNER_TENANT_LIFECYCLE,
    Permission.SERVICE_TIER_WRITE,
    Permission.DELIVERABLE_TEMPLATE_WRITE,
    Permission.PARTNER_BRAND_WRITE,
    Permission.PARTNER_WORKFLOW_TEMPLATE_WRITE,
    Permission.PARTNER_AUDIT_READ,
    Permission.PARTNER_EXPORT_EXECUTE,
];
/** Privacy scanner read permissions (Sprint 4.1.0) */
export const privacyScannerReadPermissions = [
    Permission.PRIVACY_SCANNER_READ,
];
/** Privacy scanner T2: read + export */
export const privacyScannerT2Permissions = [
    ...privacyScannerReadPermissions,
    Permission.PRIVACY_SCANNER_EXPORT,
];
/** Privacy scanner T1: full access */
export const privacyScannerT1Permissions = [
    ...privacyScannerT2Permissions,
    Permission.PRIVACY_SCANNER_WRITE,
];
/** Form Engine read permissions (Sprint 7.6.0) */
export const FORM_READ_PERMS = [
    Permission.FORMS_TEMPLATE_READ,
    Permission.FORMS_INSTANCE_READ,
];
/** Form Engine manager: read + write + export */
export const FORM_MANAGER_PERMS = [
    ...FORM_READ_PERMS,
    Permission.FORMS_INSTANCE_WRITE,
    Permission.FORMS_INSTANCE_EXPORT,
];
/** Form Engine admin: full access */
export const FORM_ADMIN_PERMS = [
    ...FORM_MANAGER_PERMS,
    Permission.FORMS_TEMPLATE_WRITE,
    Permission.FORMS_INSTANCE_APPROVE,
    Permission.FORMS_AUDIT_READ,
];
/** Group reader permissions (Sprint 11.5.0) */
export const GROUP_READ_PERMS = [
    Permission.GROUP_READ,
    Permission.GROUP_SHARED_DATA_READ,
    Permission.GROUP_AUDIT_READ,
    Permission.GROUP_DASHBOARD_READ,
    Permission.GROUP_REPORTING_READ,
    Permission.GROUP_ORG_SWITCH,
];
/** Group admin: full group access (Sprint 11.5.0) */
export const GROUP_ADMIN_PERMS = [
    ...GROUP_READ_PERMS,
    Permission.GROUP_MANAGE,
    Permission.GROUP_SHARED_DATA_MANAGE,
    Permission.GROUP_USERS_MANAGE,
    Permission.GROUP_SETTINGS_MANAGE,
];
/** Org management read permissions (Phase 9 extension) */
export const ORG_MANAGE_READ_PERMS = [
    Permission.ORG_MANAGE_READ,
    Permission.INTAKE_CONFIG_READ,
];
/** Org management admin: full org CRUD + intake config (Phase 9 extension) */
export const ORG_MANAGE_ADMIN_PERMS = [
    ...ORG_MANAGE_READ_PERMS,
    Permission.ORG_MANAGE_WRITE,
    Permission.ORG_MANAGE_DEACTIVATE,
    Permission.INTAKE_CONFIG_WRITE,
];
/** Onboarding read permissions (Sprint 1.9.0) */
const OBD_READ_PERMS = [
    Permission.ONBOARDING_WIZARD_READ,
    Permission.ONBOARDING_TOUR_READ,
    Permission.SAMPLE_DATA_READ,
    Permission.HELP_ARTICLE_READ,
];
/** Onboarding manager: read + write wizard/tours + load sample data */
const OBD_MANAGER_PERMS = [
    ...OBD_READ_PERMS,
    Permission.ONBOARDING_WIZARD_WRITE,
    Permission.ONBOARDING_TOUR_WRITE,
    Permission.SAMPLE_DATA_LOAD,
    Permission.HELP_ARTICLE_WRITE,
];
/** Onboarding admin: everything including clear sample data + delete help articles */
const OBD_ADMIN_PERMS = [
    ...OBD_MANAGER_PERMS,
    Permission.SAMPLE_DATA_CLEAR,
    Permission.HELP_ARTICLE_DELETE,
    // 44.1.0 — FAQ editor. Same admin audience as article delete.
    Permission.HELP_FAQ_EDIT,
];
/** Task Management read permissions (Sprint 17.0.0) */
const TM_READ_PERMS = [
    Permission.TM_TASK_READ,
    Permission.TM_COMMENT_READ,
    Permission.TM_CALENDAR_READ,
];
/** Task Management analyst/reviewer: read + update/complete own + comments + checklist */
const TM_ANALYST_PERMS = [
    ...TM_READ_PERMS,
    Permission.TM_TASK_UPDATE,
    Permission.TM_TASK_COMPLETE,
    Permission.TM_COMMENT_WRITE,
    Permission.TM_CHECKLIST_UPDATE,
];
/** Task Management manager: analyst + create + assign (no delete, no escalate) */
const TM_MANAGER_PERMS = [
    ...TM_ANALYST_PERMS,
    Permission.TM_TASK_CREATE,
    Permission.TM_TASK_ASSIGN,
    Permission.TM_EXPORT,
];
/** Task Management admin/DPO: all tm:* permissions */
const TM_ADMIN_PERMS = [
    ...TM_MANAGER_PERMS,
    Permission.TM_TASK_DELETE,
    Permission.TM_TASK_ESCALATE,
];
/** Task Management auditor: read-only + export */
const TM_AUDITOR_PERMS = [...TM_READ_PERMS, Permission.TM_EXPORT];
/** Workflow Engine read permissions (Sprint 17.1.0) */
const WF_READ_PERMS = [
    Permission.WF_DEFINITION_READ,
    Permission.WF_INSTANCE_READ,
    Permission.WF_TEMPLATE_READ,
];
/** Workflow Engine manager: read + transition */
const WF_MANAGER_PERMS = [
    ...WF_READ_PERMS,
    Permission.WF_INSTANCE_TRANSITION,
];
/** Workflow Engine admin: all wf:* permissions */
const WF_ADMIN_PERMS = [
    ...WF_MANAGER_PERMS,
    Permission.WF_DEFINITION_WRITE,
    Permission.WF_DEFINITION_DELETE,
    Permission.WF_DEFINITION_ACTIVATE,
    Permission.WF_TEMPLATE_WRITE,
];
/** Notification read permissions (Sprint 17.3.0) */
const NOTIF_READ_PERMS = [Permission.PLATFORM_NOTIFICATIONS_READ];
/** Notification analyst/reviewer: read + manage (own) + preferences */
const NOTIF_ANALYST_PERMS = [
    ...NOTIF_READ_PERMS,
    Permission.PLATFORM_NOTIFICATIONS_MANAGE,
    Permission.PLATFORM_NOTIFICATIONS_PREFERENCES,
];
/** Notification admin/DPO: all notification permissions */
const NOTIF_ADMIN_PERMS = [
    ...NOTIF_ANALYST_PERMS,
    Permission.PLATFORM_NOTIFICATIONS_ADMIN,
];
/** UI Assist permissions (Sprint 17.8.0) — system_admin only */
const UI_ASSIST_ADMIN_PERMS = [
    Permission.UI_ASSIST_VIEW,
    Permission.UI_ASSIST_EXECUTE,
];
/** Agent Lobby permissions — system_admin only (UI Assist extension) */
const AGENT_LOBBY_ADMIN_PERMS = [
    Permission.AGENT_LOBBY_VIEW,
    Permission.AGENT_LOBBY_MANAGE,
    Permission.AGENT_LOBBY_DECIDE,
];
// Sprint 18.0.0: License & Package Management permission groups
const LIC_READ_PERMS = [Permission.LICENSE_READ, Permission.PACKAGE_READ];
const LIC_ADMIN_PERMS = [
    ...LIC_READ_PERMS,
    Permission.LICENSE_ACTIVATE,
    Permission.LICENSE_DEACTIVATE,
    Permission.PACKAGE_UPGRADE,
    Permission.PACKAGE_DOWNGRADE,
];
// ── Sprint 47.5.0 — Forseti permission helper arrays ─────────────────
// All Forseti permissions are available on all tiers (Light/Standard/
// Enterprise) per family plan §9.1 Q2. Helper arrays group by role
// archetype rather than by tier.
/** Read-only Forseti permissions for VIEWER + AUDITOR roles. */
export const FORSETI_READ_PERMS = [
    Permission.AI_ADVISOR_CHAT, // Read implicit — chat permission also covers reading own chats
    Permission.AI_PROJECTS_READ,
    Permission.AI_WORKFLOWS_READ,
    Permission.AI_WORKFLOWS_SYSTEM_PACK_READ,
    Permission.AI_TABULAR_REVIEWS_READ,
    Permission.AI_PROVIDER_VIEW_USAGE,
    Permission.AI_PROVIDER_VIEW_AUDIT,
    // Sprint 48.0.0c — memory browse is part of the read baseline so all
    // Forseti users can see what context the org has accumulated.
    Permission.AI_ADVISOR_MEMORY_READ,
];
/** Standard write permissions for CONTRIBUTOR role (no admin/delete/configure). */
export const FORSETI_WRITE_PERMS = [
    ...FORSETI_READ_PERMS,
    Permission.AI_ADVISOR_CHAT_SHARE,
    Permission.AI_ADVISOR_CHAT_EXPORT,
    // Sprint 48.0.0c — contributors can edit / delete their own memories +
    // those auto-inferred from their chats. Promote-to-curated stays admin.
    Permission.AI_ADVISOR_MEMORY_WRITE,
    Permission.AI_PROJECTS_WRITE,
    Permission.AI_PROJECTS_SHARE,
    Permission.AI_WORKFLOWS_WRITE,
    Permission.AI_WORKFLOWS_RUN,
    Permission.AI_WORKFLOWS_SHARE,
    Permission.AI_WORKFLOWS_SYSTEM_PACK_HIDE,
    Permission.AI_TABULAR_REVIEWS_WRITE,
    Permission.AI_TABULAR_REVIEWS_RUN,
    Permission.AI_TABULAR_REVIEWS_SHARE,
    Permission.AI_DOCUMENT_REVIEW_PROPOSE_EDIT,
    Permission.AI_DOCUMENT_REVIEW_ACCEPT_EDIT,
    Permission.AI_DOCUMENT_REVIEW_REJECT_EDIT,
    Permission.AI_DOCUMENT_REVIEW_VERSION_PROMOTE,
];
/** Manager-level Forseti permissions (CASE_MANAGER + COMPLIANCE_OWNER). */
export const FORSETI_MANAGER_PERMS = [
    ...FORSETI_WRITE_PERMS,
    Permission.AI_PROJECTS_DELETE,
    Permission.AI_WORKFLOWS_DELETE,
    Permission.AI_TABULAR_REVIEWS_DELETE,
];
/** Full Forseti permissions including provider configuration + quota
 *  override (PLATFORM_ADMIN only). */
export const FORSETI_ADMIN_PERMS = [
    ...FORSETI_MANAGER_PERMS,
    Permission.AI_ADVISOR_ADMIN,
    // Sprint 48.0.0c — promote-to-curated is admin-only (curating locks
    // a memory against retention GC).
    Permission.AI_ADVISOR_MEMORY_CURATE,
    Permission.AI_PROJECTS_ADMIN,
    Permission.AI_WORKFLOWS_ADMIN,
    Permission.AI_TABULAR_REVIEWS_ADMIN,
    Permission.AI_DOCUMENT_REVIEW_ADMIN,
    Permission.AI_PROVIDER_CONFIGURE,
    Permission.AI_PROVIDER_QUOTA_OVERRIDE,
    // Sprint 48.5.x — self-service billing (buy + redeem token bundles) is
    // admin-only, mirroring quota override: it touches the org's paid capacity.
    Permission.AI_PROVIDER_BILLING,
    // Sprint 47.9.7 — MCP key management is admin-only, mirroring the
    // BYOK provider-config permission. The :list permission is also
    // admin-only because the inventory reveals key labels + creation
    // timestamps that aid lateral-movement reconnaissance.
    Permission.AI_PROVIDER_MCP_CREATE,
    Permission.AI_PROVIDER_MCP_REVOKE,
    Permission.AI_PROVIDER_MCP_LIST,
];
/**
 * Default permissions per role.
 * See: docs/product/phases/phase-1/sprint-1.2.0.md - RBAC
 */
export const ROLE_PERMISSIONS = {
    [SystemRole.PLATFORM_ADMIN]: [
        Permission.CASE_CREATE,
        Permission.CASE_READ,
        Permission.CASE_UPDATE,
        Permission.CASE_DELETE,
        Permission.CASE_ASSIGN,
        Permission.CASE_REASSIGN,
        Permission.CASE_COMMENT,
        Permission.CASE_ATTACH,
        Permission.CASE_ATTACH_DOWNLOAD,
        Permission.CASE_IMPORT,
        Permission.DSR_INTAKE_CONFIGURE,
        Permission.DSR_SLA_CONFIGURE,
        Permission.AUDIT_EVENTS_READ,
        Permission.AUDIT_EVENTS_SEARCH,
        Permission.AUDIT_DASHBOARD_READ,
        Permission.DASHBOARD_READ,
        Permission.PLATFORM_SETTINGS_READ,
        Permission.PLATFORM_SETTINGS_WRITE,
        Permission.PLATFORM_USERS_READ,
        Permission.PLATFORM_USERS_WRITE,
        Permission.PLATFORM_USERS_ROLES_ASSIGN,
        Permission.PLATFORM_MFA_READ,
        Permission.PLATFORM_MFA_WRITE,
        Permission.PLATFORM_SSO_READ,
        Permission.PLATFORM_SSO_WRITE,
        Permission.PLATFORM_API_KEYS_READ,
        Permission.PLATFORM_API_KEYS_WRITE,
        Permission.PLATFORM_DELEGATIONS_READ,
        Permission.PLATFORM_DELEGATIONS_WRITE,
        Permission.PLATFORM_BREAKGLASS_REQUEST,
        Permission.PLATFORM_BREAKGLASS_APPROVE,
        Permission.PLATFORM_ACCESS_REVIEW_READ,
        Permission.PLATFORM_ACCESS_REVIEW_WRITE,
        Permission.PLATFORM_ACCESS_REVIEW_DECIDE,
        Permission.PLATFORM_ATTESTATION_READ,
        Permission.PLATFORM_ATTESTATION_WRITE,
        Permission.PLATFORM_SOD_READ,
        Permission.PLATFORM_SOD_WRITE,
        Permission.TEMPLATE_READ,
        Permission.TEMPLATE_WRITE,
        Permission.CASE_ATTACH_DELETE,
        Permission.CASE_CHECKLIST_UPDATE,
        Permission.CASE_REFERENCE_ADD,
        Permission.CASE_CLOSURE_SUBMIT,
        Permission.CASE_CLOSURE_APPROVE,
        Permission.CASE_EXPORT_PDF,
        Permission.CASE_EXPORT_ZIP,
        Permission.SLA_CONFIG_READ,
        Permission.SLA_CONFIG_WRITE,
        ...DM_ADMIN_PERMS,
        ...GOV_ADMIN_PERMS,
        ...CONSENT_ADMIN_PERMS,
        ...ASSESS_ADMIN_PERMS,
        ...CLASS_ADMIN_PERMS,
        ...MON_ADMIN_PERMS,
        ...INC_ADMIN_PERMS,
        ...OBD_ADMIN_PERMS,
        ...CONN_ADMIN_PERMS,
        ...VR_ADMIN_PERMS,
        ...DELORCH_ADMIN_PERMS,
        ...DISC_ADMIN_PERMS,
        ...INCOPS_ADMIN_PERMS,
        ...PGOV_ADMIN_PERMS,
        ...ACCT_ADMIN_PERMS,
        ...CPROP_ADMIN_PERMS,
        ...L10N_ADMIN_PERMS,
        ...PM_ADMIN_PERMS,
        // Sprint 3.6.0: all 9 new permissions
        Permission.ATTACHMENTS_UPLOAD,
        Permission.ATTACHMENTS_DOWNLOAD,
        Permission.ATTACHMENTS_DELETE,
        Permission.PLATFORM_WEBHOOKS_READ,
        Permission.PLATFORM_WEBHOOKS_WRITE,
        Permission.PLATFORM_DOMAINS_READ,
        Permission.PLATFORM_DOMAINS_WRITE,
        Permission.PLATFORM_EXPORT_SCHEDULES_READ,
        Permission.PLATFORM_EXPORT_SCHEDULES_WRITE,
        // Sprint 4.1.0: privacy scanner
        ...privacyScannerT1Permissions,
        // Sprint 4.4.0: module configuration
        Permission.PLATFORM_MODULE_CONFIG_READ,
        Permission.PLATFORM_MODULE_CONFIG_WRITE,
        // Sprint 7.6.0: form engine
        ...FORM_ADMIN_PERMS,
        // Sprint 11.5.0: group management
        ...GROUP_ADMIN_PERMS,
        // Phase 9 extension: org management + intake config
        ...ORG_MANAGE_ADMIN_PERMS,
        // Sprint 17.0.0: task management
        ...TM_ADMIN_PERMS,
        // Sprint 17.1.0: workflow engine
        ...WF_ADMIN_PERMS,
        // Sprint 17.3.0: notifications
        ...NOTIF_ADMIN_PERMS,
        // Sprint 17.8.0: UI Assist (system_admin only)
        ...UI_ASSIST_ADMIN_PERMS,
        // Agent Lobby: multi-session coordination (system_admin only)
        ...AGENT_LOBBY_ADMIN_PERMS,
        // Sprint 18.0.0: license & package management
        ...LIC_ADMIN_PERMS,
        // Sprint 21.1.0: incident response maturity
        ...incidentBreachNotificationReadTier,
        ...incidentBreachNotificationWriteTier,
        // Sprint 21.2.0: incident ops maturity
        ...incidentOpsMaturityReadTier,
        ...incidentOpsMaturityWriteTier,
        // Sprint 40.0.0-40.2.0: import, trigger config, data subjects
        Permission.CONSENT_WORKFLOW_IMPORT,
        Permission.ASSESSMENT_TRIGGER_CONFIGURE,
        Permission.DATA_SUBJECT_CREATE,
        Permission.DATA_SUBJECT_READ,
        Permission.DATA_SUBJECT_UPDATE,
        Permission.DATA_SUBJECT_DELETE,
        Permission.DATA_SUBJECT_LINK,
        // Sprint 41.4.0 F1 — Platform Files (full set: configure, browse,
        // delete, restore, hard-delete, legal-hold override, audit, metrics)
        Permission.PLATFORM_FILES_CONFIG_READ,
        Permission.PLATFORM_FILES_CONFIG_WRITE,
        Permission.PLATFORM_FILES_BROWSE,
        Permission.PLATFORM_FILES_DETAIL_READ,
        Permission.PLATFORM_FILES_SOFT_DELETE,
        Permission.PLATFORM_FILES_RESTORE,
        Permission.PLATFORM_FILES_HARD_DELETE,
        Permission.PLATFORM_FILES_AUDIT_READ,
        Permission.PLATFORM_FILES_METRICS_READ,
        Permission.PLATFORM_FILES_LEGAL_HOLD_OVERRIDE,
        // Sprint 43.2.0 batch 1 — all four new admin/breach permissions land
        // on PLATFORM_ADMIN per the role-tier mapping in the sprint spec.
        Permission.CASE_TEMPLATE_ADMIN,
        Permission.ORG_DEPARTMENT_ADMIN,
        Permission.EXPORT_BULK,
        Permission.BREACH_FLAG,
        // Sprint 47.4.0 — Immutable Audit Trails (full set: read settings +
        // proof status + integrity center, configure taxonomy/caps/credentials,
        // export Integrity Packs, manage credit-package billing).
        Permission.IMMUTABLE_AUDIT_TRAILS_READ,
        Permission.IMMUTABLE_AUDIT_TRAILS_CONFIGURE,
        Permission.IMMUTABLE_AUDIT_TRAILS_EXPORT,
        Permission.IMMUTABLE_AUDIT_TRAILS_BILLING,
        // Sprint 47.5.0 — Forseti AI Legal Advisor (full admin)
        ...FORSETI_ADMIN_PERMS,
    ],
    [SystemRole.COMPLIANCE_OWNER]: [
        Permission.CASE_CREATE,
        Permission.CASE_READ,
        Permission.CASE_UPDATE,
        Permission.CASE_DELETE,
        Permission.CASE_ASSIGN,
        Permission.CASE_REASSIGN,
        Permission.CASE_COMMENT,
        Permission.CASE_ATTACH,
        Permission.CASE_ATTACH_DOWNLOAD,
        Permission.CASE_IMPORT,
        Permission.DSR_INTAKE_CONFIGURE,
        Permission.DSR_IDENTITY_VERIFY,
        Permission.DSR_RESPONSE_DRAFT,
        Permission.DSR_RESPONSE_SEND,
        Permission.DSR_SLA_CONFIGURE,
        Permission.DSR_SLA_OVERRIDE,
        Permission.DSR_DATA_COLLECTION_REQUEST,
        Permission.DSR_DELETION_REQUEST,
        Permission.DSR_DELETION_CONFIRM,
        Permission.AUDIT_EVENTS_READ,
        Permission.AUDIT_EVENTS_SEARCH,
        Permission.AUDIT_EVENTS_EXPORT,
        Permission.AUDIT_DASHBOARD_READ,
        Permission.DASHBOARD_READ,
        Permission.PLATFORM_SETTINGS_READ,
        Permission.PLATFORM_USERS_READ,
        Permission.PLATFORM_MFA_READ,
        Permission.PLATFORM_SSO_READ,
        Permission.PLATFORM_API_KEYS_READ,
        Permission.PLATFORM_API_KEYS_WRITE,
        Permission.PLATFORM_DELEGATIONS_READ,
        Permission.PLATFORM_DELEGATIONS_WRITE,
        Permission.PLATFORM_BREAKGLASS_REQUEST,
        Permission.PLATFORM_BREAKGLASS_APPROVE,
        Permission.PLATFORM_ACCESS_REVIEW_READ,
        Permission.PLATFORM_ACCESS_REVIEW_WRITE,
        Permission.PLATFORM_ACCESS_REVIEW_DECIDE,
        Permission.PLATFORM_ATTESTATION_READ,
        Permission.PLATFORM_ATTESTATION_WRITE,
        Permission.PLATFORM_SOD_READ,
        Permission.PLATFORM_SOD_WRITE,
        Permission.TEMPLATE_READ,
        Permission.TEMPLATE_WRITE,
        Permission.CASE_ATTACH_DELETE,
        Permission.CASE_CHECKLIST_UPDATE,
        Permission.CASE_REFERENCE_ADD,
        Permission.CASE_CLOSURE_SUBMIT,
        Permission.CASE_CLOSURE_APPROVE,
        Permission.CASE_EXPORT_PDF,
        Permission.CASE_EXPORT_ZIP,
        Permission.SLA_CONFIG_READ,
        Permission.SLA_CONFIG_WRITE,
        ...DM_ADMIN_PERMS,
        ...GOV_ADMIN_PERMS,
        ...CONSENT_ADMIN_PERMS,
        ...ASSESS_ADMIN_PERMS,
        ...CLASS_ADMIN_PERMS,
        ...MON_ADMIN_PERMS,
        ...INC_ADMIN_PERMS,
        ...OBD_ADMIN_PERMS,
        ...CONN_ADMIN_PERMS,
        ...VR_ADMIN_PERMS,
        ...DELORCH_ADMIN_PERMS,
        ...DISC_ADMIN_PERMS,
        ...INCOPS_ADMIN_PERMS,
        ...PGOV_ADMIN_PERMS,
        ...ACCT_ADMIN_PERMS,
        ...CPROP_ADMIN_PERMS,
        ...L10N_ADMIN_PERMS,
        ...PM_ADMIN_PERMS,
        // Sprint 3.6.0
        Permission.ATTACHMENTS_UPLOAD,
        Permission.ATTACHMENTS_DOWNLOAD,
        Permission.ATTACHMENTS_DELETE,
        Permission.PLATFORM_WEBHOOKS_READ,
        Permission.PLATFORM_EXPORT_SCHEDULES_READ,
        Permission.PLATFORM_EXPORT_SCHEDULES_WRITE,
        // Sprint 4.1.0: privacy scanner
        ...privacyScannerT1Permissions,
        // Sprint 7.6.0: form engine
        ...FORM_ADMIN_PERMS,
        // Sprint 11.5.0: group management
        ...GROUP_ADMIN_PERMS,
        // Phase 9 extension: org management + intake config
        ...ORG_MANAGE_ADMIN_PERMS,
        // Sprint 17.0.0: task management
        ...TM_ADMIN_PERMS,
        // Sprint 17.1.0: workflow engine
        ...WF_ADMIN_PERMS,
        // Sprint 17.3.0: notifications
        ...NOTIF_ADMIN_PERMS,
        // Sprint 18.0.0: license & package management (read-only)
        ...LIC_READ_PERMS,
        // Sprint 21.1.0: incident response maturity
        ...incidentBreachNotificationReadTier,
        ...incidentBreachNotificationWriteTier,
        // Sprint 21.2.0: incident ops maturity
        ...incidentOpsMaturityReadTier,
        ...incidentOpsMaturityWriteTier,
        // Sprint 40.0.0-40.2.0: import, trigger config, data subjects
        Permission.CONSENT_WORKFLOW_IMPORT,
        Permission.ASSESSMENT_TRIGGER_CONFIGURE,
        Permission.DATA_SUBJECT_CREATE,
        Permission.DATA_SUBJECT_READ,
        Permission.DATA_SUBJECT_UPDATE,
        Permission.DATA_SUBJECT_DELETE,
        Permission.DATA_SUBJECT_LINK,
        // Sprint 41.4.0 F1 — Platform Files (DPO-equivalent role: read
        // settings, browse, see details, soft-delete with reason, view
        // audit trail and metrics; NO hard delete and NO legal-hold
        // override — those are platform_admin-only)
        Permission.PLATFORM_FILES_CONFIG_READ,
        Permission.PLATFORM_FILES_BROWSE,
        Permission.PLATFORM_FILES_DETAIL_READ,
        Permission.PLATFORM_FILES_SOFT_DELETE,
        Permission.PLATFORM_FILES_RESTORE,
        Permission.PLATFORM_FILES_AUDIT_READ,
        Permission.PLATFORM_FILES_METRICS_READ,
        // Sprint 43.2.0 batch 1 — DPO gets template admin + bulk export +
        // breach flag; department admin stays PLATFORM_ADMIN-only.
        Permission.CASE_TEMPLATE_ADMIN,
        Permission.EXPORT_BULK,
        Permission.BREACH_FLAG,
        // Sprint 47.4.0 — Immutable Audit Trails (DPO-equivalent: read +
        // configure + export; billing stays PLATFORM_ADMIN-only).
        Permission.IMMUTABLE_AUDIT_TRAILS_READ,
        Permission.IMMUTABLE_AUDIT_TRAILS_CONFIGURE,
        Permission.IMMUTABLE_AUDIT_TRAILS_EXPORT,
        // Sprint 47.5.0 — Forseti AI Legal Advisor (manager-level)
        ...FORSETI_MANAGER_PERMS,
    ],
    [SystemRole.CASE_MANAGER]: [
        Permission.CASE_CREATE,
        Permission.CASE_READ,
        Permission.CASE_UPDATE,
        Permission.CASE_ASSIGN,
        Permission.CASE_REASSIGN,
        Permission.CASE_COMMENT,
        Permission.CASE_ATTACH,
        Permission.CASE_ATTACH_DOWNLOAD,
        Permission.DSR_IDENTITY_VERIFY,
        Permission.DSR_RESPONSE_DRAFT,
        Permission.DSR_RESPONSE_SEND,
        Permission.DSR_DATA_COLLECTION_REQUEST,
        Permission.DSR_DELETION_REQUEST,
        Permission.AUDIT_EVENTS_READ,
        Permission.AUDIT_DASHBOARD_READ,
        Permission.DASHBOARD_READ,
        Permission.PLATFORM_USERS_READ,
        Permission.TEMPLATE_READ,
        Permission.CASE_CHECKLIST_UPDATE,
        Permission.CASE_REFERENCE_ADD,
        Permission.CASE_CLOSURE_SUBMIT,
        Permission.CASE_CLOSURE_APPROVE,
        Permission.CASE_EXPORT_PDF,
        Permission.SLA_CONFIG_READ,
        Permission.PLATFORM_DELEGATIONS_READ,
        Permission.PLATFORM_DELEGATIONS_WRITE,
        Permission.PLATFORM_BREAKGLASS_REQUEST,
        Permission.PLATFORM_ACCESS_REVIEW_READ,
        Permission.PLATFORM_ACCESS_REVIEW_DECIDE,
        ...DM_MANAGER_PERMS,
        ...GOV_MANAGER_PERMS,
        ...CONSENT_MANAGER_PERMS,
        ...ASSESS_MANAGER_PERMS,
        ...CLASS_MANAGER_PERMS,
        ...MON_MANAGER_PERMS,
        ...INC_MANAGER_PERMS,
        ...OBD_MANAGER_PERMS,
        ...CONN_MANAGER_PERMS,
        ...VR_MANAGER_PERMS,
        ...DELORCH_MANAGER_PERMS,
        ...DISC_MANAGER_PERMS,
        ...INCOPS_MANAGER_PERMS,
        ...PGOV_MANAGER_PERMS,
        ...ACCT_MANAGER_PERMS,
        ...CPROP_MANAGER_PERMS,
        ...L10N_MANAGER_PERMS,
        ...PM_MANAGER_PERMS,
        // Sprint 3.6.0
        Permission.ATTACHMENTS_UPLOAD,
        Permission.ATTACHMENTS_DOWNLOAD,
        Permission.PLATFORM_EXPORT_SCHEDULES_READ,
        // Sprint 4.1.0: privacy scanner
        ...privacyScannerT2Permissions,
        // Sprint 7.6.0: form engine
        ...FORM_MANAGER_PERMS,
        // Sprint 17.0.0: task management
        ...TM_MANAGER_PERMS,
        // Sprint 17.1.0: workflow engine
        ...WF_MANAGER_PERMS,
        // Sprint 17.3.0: notifications
        ...NOTIF_ANALYST_PERMS,
        // Sprint 18.0.0: package read
        Permission.PACKAGE_READ,
        // Sprint 21.2.0: incident ops maturity
        ...incidentOpsMaturityReadTier,
        ...incidentOpsMaturityWriteTier,
        // Sprint 40.0.0-40.2.0: import, trigger config, data subjects
        Permission.CONSENT_WORKFLOW_IMPORT,
        Permission.ASSESSMENT_TRIGGER_CONFIGURE,
        Permission.DATA_SUBJECT_CREATE,
        Permission.DATA_SUBJECT_READ,
        Permission.DATA_SUBJECT_UPDATE,
        Permission.DATA_SUBJECT_DELETE,
        Permission.DATA_SUBJECT_LINK,
        // Sprint 41.4.0 F1 — Platform Files (compliance manager: browse,
        // see details, view metrics — read-only with no audit access)
        Permission.PLATFORM_FILES_BROWSE,
        Permission.PLATFORM_FILES_DETAIL_READ,
        Permission.PLATFORM_FILES_METRICS_READ,
        // Sprint 43.2.0 batch 1 — Case Manager needs bulk export for daily
        // operations (CSV / PDF / ZIP on TicketListPage). No template admin
        // (DPO-governed) and no breach flag (DPO-only).
        Permission.EXPORT_BULK,
        // Sprint 47.5.0 — Forseti AI Legal Advisor (manager-level)
        ...FORSETI_MANAGER_PERMS,
    ],
    [SystemRole.AUDITOR]: [
        Permission.CASE_READ,
        Permission.CASE_ATTACH_DOWNLOAD,
        Permission.AUDIT_EVENTS_READ,
        Permission.AUDIT_EVENTS_SEARCH,
        Permission.AUDIT_EVENTS_EXPORT,
        Permission.AUDIT_DASHBOARD_READ,
        Permission.DASHBOARD_READ,
        Permission.PLATFORM_SETTINGS_READ,
        Permission.PLATFORM_USERS_READ,
        Permission.PLATFORM_API_KEYS_READ,
        Permission.PLATFORM_DELEGATIONS_READ,
        Permission.PLATFORM_BREAKGLASS_REQUEST,
        Permission.PLATFORM_ACCESS_REVIEW_READ,
        Permission.PLATFORM_ATTESTATION_READ,
        Permission.PLATFORM_SOD_READ,
        Permission.TEMPLATE_READ,
        ...DM_READ_PERMS,
        Permission.DM_AUDIT_READ,
        // Sprint 45.4.0 — auditors export Data Registry for regulator inspections.
        Permission.DM_REGISTRY_EXPORT,
        ...GOV_READ_PERMS,
        Permission.GOV_AUDIT_READ,
        ...CONSENT_READ_PERMS,
        Permission.CONSENT_EXPORT_EXECUTE,
        ...ASSESS_READ_PERMS,
        // Sprint 44.3.1 Epic C — ASSESSMENT_EXPORT_EXECUTE removed (dead perm).
        ...CLASS_READ_PERMS,
        Permission.CLASSIFICATION_EXPORT_EXECUTE,
        ...MON_READ_PERMS,
        // Sprint 44.3.1 Epic C — PROGRAM_EXPORT_EXECUTE removed (dead perm;
        // superseded by monitoring_export:execute).
        ...INC_READ_PERMS,
        Permission.INCIDENT_EXPORT_EXECUTE,
        ...OBD_READ_PERMS,
        ...CONN_READ_PERMS,
        ...VR_READ_PERMS,
        Permission.VENDOR_RISK_EXPORT_EXECUTE,
        ...DELORCH_READ_PERMS,
        Permission.DELETION_REPORT_GENERATE,
        ...DISC_READ_PERMS,
        Permission.DISCOVERY_EXPORT_EXECUTE,
        ...INCOPS_READ_PERMS,
        Permission.INCIDENT_METRICS_EXPORT,
        ...PGOV_READ_PERMS,
        Permission.GOVERNANCE_EXPORT_EXECUTE,
        ...ACCT_READ_PERMS,
        Permission.ACCESS_LOG_EXPORT,
        Permission.ACCESS_INVESTIGATION_EXPORT,
        ...CPROP_READ_PERMS,
        Permission.CONSENT_CROSS_PROPERTY_EXPORT,
        ...L10N_READ_PERMS,
        Permission.LOCALIZATION_EXPORT_EXECUTE,
        ...PM_READ_PERMS,
        Permission.PARTNER_AUDIT_READ,
        // Sprint 3.6.0
        Permission.ATTACHMENTS_DOWNLOAD,
        // Sprint 43.5.7 — the generic client-csv-export endpoint requires
        // ATTACHMENTS_UPLOAD because it persists the produced file to the
        // Files Browser. AUDITOR has explicit *_EXPORT permissions for every
        // module (AUDIT_EVENTS_EXPORT, GOVERNANCE_EXPORT_EXECUTE, etc.), so
        // granting the generic upload permission is consistent with the
        // role's intent and fixes Governance Audit Trail CSV/XLSX export
        // silently failing with 403.
        Permission.ATTACHMENTS_UPLOAD,
        Permission.PLATFORM_WEBHOOKS_READ,
        Permission.PLATFORM_EXPORT_SCHEDULES_READ,
        // Sprint 4.1.0: privacy scanner
        ...privacyScannerReadPermissions,
        Permission.PRIVACY_SCANNER_EXPORT,
        // Sprint 7.6.0: form engine (read + audit)
        ...FORM_READ_PERMS,
        Permission.FORMS_AUDIT_READ,
        // Sprint 17.0.0: task management (auditor)
        ...TM_AUDITOR_PERMS,
        // Sprint 17.1.0: workflow engine (read-only)
        ...WF_READ_PERMS,
        // Sprint 17.3.0: notifications (read-only)
        ...NOTIF_READ_PERMS,
        // Sprint 18.0.0: license & package management (read-only)
        ...LIC_READ_PERMS,
        // Sprint 21.2.0: incident ops maturity (read-only)
        ...incidentOpsMaturityReadTier,
        // Sprint 40.0.0-40.2.0: import, trigger config, data subjects
        Permission.CONSENT_WORKFLOW_IMPORT,
        Permission.ASSESSMENT_TRIGGER_CONFIGURE,
        Permission.DATA_SUBJECT_CREATE,
        Permission.DATA_SUBJECT_READ,
        Permission.DATA_SUBJECT_UPDATE,
        Permission.DATA_SUBJECT_DELETE,
        Permission.DATA_SUBJECT_LINK,
        // Sprint 41.4.0 F1 — Platform Files (Internal Auditor / P-VN-10
        // pattern: full read on browse + detail + audit trail + metrics;
        // explicitly NO write/delete/restore/legal-hold capabilities)
        Permission.PLATFORM_FILES_CONFIG_READ,
        Permission.PLATFORM_FILES_BROWSE,
        Permission.PLATFORM_FILES_DETAIL_READ,
        Permission.PLATFORM_FILES_AUDIT_READ,
        Permission.PLATFORM_FILES_METRICS_READ,
        // Sprint 47.4.0 — Immutable Audit Trails (auditor: read proof state
        // + export Integrity Packs; explicitly NO configure or billing).
        Permission.IMMUTABLE_AUDIT_TRAILS_READ,
        Permission.IMMUTABLE_AUDIT_TRAILS_EXPORT,
        // Sprint 47.5.0 — Forseti AI Legal Advisor (read-only — auditor view)
        ...FORSETI_READ_PERMS,
    ],
    [SystemRole.SUPPORT]: [
        Permission.CASE_READ,
        Permission.CASE_COMMENT,
        Permission.CASE_ATTACH,
        Permission.CASE_ATTACH_DOWNLOAD,
        Permission.TEMPLATE_READ,
        Permission.PLATFORM_USERS_READ,
        Permission.CASE_REFERENCE_ADD,
        // Sprint 3.6.0
        Permission.ATTACHMENTS_DOWNLOAD,
        // Sprint 7.6.0: form engine (read-only)
        ...FORM_READ_PERMS,
        // Sprint 29.2.0: dashboard access
        Permission.DASHBOARD_READ,
        // Sprint 40.0.0-40.2.0: import, trigger config, data subjects
        Permission.CONSENT_WORKFLOW_IMPORT,
        Permission.ASSESSMENT_TRIGGER_CONFIGURE,
        Permission.DATA_SUBJECT_CREATE,
        Permission.DATA_SUBJECT_READ,
        Permission.DATA_SUBJECT_UPDATE,
        Permission.DATA_SUBJECT_DELETE,
        Permission.DATA_SUBJECT_LINK,
        // Sprint 47.5.0 — Forseti AI Legal Advisor (chat-only)
        Permission.AI_ADVISOR_CHAT,
        Permission.AI_PROJECTS_READ,
    ],
    // Sprint 1.2.0 + 1.3.0 roles
    [SystemRole.CONTRIBUTOR]: [
        ...DM_CONTRIBUTOR_PERMS,
        ...GOV_CONTRIBUTOR_PERMS,
        // Sprint 3.6.0
        Permission.ATTACHMENTS_UPLOAD,
        Permission.ATTACHMENTS_DOWNLOAD,
        // Sprint 29.2.0: dashboard access
        Permission.DASHBOARD_READ,
        // Sprint 40.0.0-40.2.0: import, trigger config, data subjects
        Permission.CONSENT_WORKFLOW_IMPORT,
        Permission.ASSESSMENT_TRIGGER_CONFIGURE,
        Permission.DATA_SUBJECT_CREATE,
        Permission.DATA_SUBJECT_READ,
        Permission.DATA_SUBJECT_UPDATE,
        Permission.DATA_SUBJECT_DELETE,
        Permission.DATA_SUBJECT_LINK,
        // Sprint 47.5.0 — Forseti AI Legal Advisor (write-level, no admin)
        ...FORSETI_WRITE_PERMS,
    ],
    [SystemRole.VIEWER]: [
        ...DM_READ_PERMS,
        ...GOV_READ_PERMS,
        ...CONSENT_READ_PERMS,
        ...ASSESS_READ_PERMS,
        ...CLASS_READ_PERMS,
        ...MON_READ_PERMS,
        ...INC_READ_PERMS,
        ...OBD_READ_PERMS,
        ...CONN_READ_PERMS,
        ...VR_READ_PERMS,
        ...DELORCH_READ_PERMS,
        ...DISC_READ_PERMS,
        ...INCOPS_READ_PERMS,
        ...PGOV_READ_PERMS,
        ...ACCT_READ_PERMS,
        ...CPROP_READ_PERMS,
        ...L10N_READ_PERMS,
        ...PM_READ_PERMS,
        // Sprint 3.6.0
        Permission.ATTACHMENTS_DOWNLOAD,
        // Sprint 4.1.0: privacy scanner
        ...privacyScannerReadPermissions,
        // Sprint 7.6.0: form engine (read-only)
        ...FORM_READ_PERMS,
        // Sprint 17.0.0: task management (read-only)
        ...TM_READ_PERMS,
        // Sprint 17.1.0: workflow engine (read-only)
        ...WF_READ_PERMS,
        // Sprint 17.3.0: notifications (read-only)
        ...NOTIF_READ_PERMS,
        // Sprint 18.0.0: license & package management (read-only)
        ...LIC_READ_PERMS,
        // Sprint 21.2.0: incident ops maturity (read-only)
        ...incidentOpsMaturityReadTier,
        // Sprint 29.2.0: dashboard access
        Permission.DASHBOARD_READ,
        // Sprint 40.0.0-40.2.0: import, trigger config, data subjects
        Permission.CONSENT_WORKFLOW_IMPORT,
        Permission.ASSESSMENT_TRIGGER_CONFIGURE,
        Permission.DATA_SUBJECT_CREATE,
        Permission.DATA_SUBJECT_READ,
        Permission.DATA_SUBJECT_UPDATE,
        Permission.DATA_SUBJECT_DELETE,
        Permission.DATA_SUBJECT_LINK,
        // Sprint 47.5.0 — Forseti AI Legal Advisor (read-only)
        ...FORSETI_READ_PERMS,
    ],
};
//# sourceMappingURL=permissions.js.map